import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, LinearProgress, Paper, Step, StepLabel, Stepper, TextField, Typography, useMediaQuery } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CreateNewFolderIcon from '@mui/icons-material/CreateNewFolder';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import HubIcon from '@mui/icons-material/Hub';
import MicIcon from '@mui/icons-material/Mic';
import MicExternalOnIcon from '@mui/icons-material/InterpreterMode';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import StopIcon from '@mui/icons-material/Stop';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material/styles';
import { useWebSocket } from './WebSocketContext';
import LoadingScreen from './LoadingScreen';
import CreationSample from './CreationSample';
import FirstResultSetup from './FirstResultSetup';
import TemplateSelectionDialog from './TemplateSelectionDialog';
import AudioReviewDialog from './AudioReviewDialog';
import RecordingDialog from './RecordingDialog';
import FlowCreatorHome from './FlowCreatorHome';
import useAudioRecorder from './useAudioRecorder';
import {
  clearCaseCreation,
  isCaseCreationActive,
  startCaseCreation,
} from '../Utils/caseCreationState';
import { apiRequest } from '../Utils/apiClient';
import { DASHBOARD_PROJECT_VIEW, FLOWS_PROJECT_VIEW, saveProjectViewForSession } from '../Utils/projectViewSession';
import { clearFirstResultDraft, FIRST_RESULT_GOAL_MAX_LENGTH, readFirstResultDraft, saveFirstResultDraft } from '../Utils/firstResultDraft';
import { notifyProjectCreated } from '../Utils/projectEvents';
import logo from '../images/logowithname2.png';

const ConnectorWizardHost = React.lazy(() => import('./ConnectorWizardHost'));
const SettingDatasources = React.lazy(() => import('./SettingDatasources'));

const primaryColor = '#5757b0';
const primaryColorTransparent = 'rgba(87, 87, 176, 0.8)';
const hoverColor = '#7777d0';
const selectedColor = '#4545a0';
const TEMP_PROJECT_TITLE = '__temporary_project__';
const TEMP_PROJECT_DESCRIPTION = '__temporary_project_description__';
const BOOTSTRAP_TARGET_COUNT = 5;
const BOOTSTRAP_REPAIR_MAX_ATTEMPTS = 3;
const BOOTSTRAP_TIMEOUT_MS = 120000;
const datasourceCardSx = {
  p: { xs: 3, sm: 4 },
  borderRadius: '24px',
  width: '100%',
  background: 'linear-gradient(135deg, rgba(255,255,255,0.98) 0%, rgba(247,247,255,0.96) 100%)',
  boxShadow: '0 10px 30px rgba(87, 87, 176, 0.08)',
  border: '2px solid rgba(87, 87, 176, 0.22)',
  minHeight: { xs: 'unset', lg: '100%' },
  display: 'flex',
  flexDirection: 'column',
  transition: 'all 0.25s ease',
};
const datasourceCardHeaderSx = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 1.5,
  mb: 2,
  width: '100%',
};
const datasourceCardBodySx = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
};
const datasourceCardSubtitleSx = {
  color: 'text.secondary',
  lineHeight: 1.7,
  maxWidth: 320,
  minHeight: 48,
  mb: 2,
};
const wizardLauncherButtonSx = {
  minWidth: 0,
  width: '100%',
  maxWidth: 320,
  px: 2.75,
  py: 1.35,
  borderRadius: '999px',
  textTransform: 'none',
  fontWeight: 700,
  fontSize: '0.95rem',
  lineHeight: 1.2,
  color: primaryColor,
  borderColor: 'rgba(87, 87, 176, 0.25)',
  background: 'linear-gradient(135deg, rgba(255,255,255,0.98) 0%, rgba(247,247,255,0.96) 100%)',
  boxShadow: '0 8px 24px rgba(87, 87, 176, 0.08)',
  '&:hover': {
    borderColor: primaryColor,
    background: 'linear-gradient(135deg, rgba(255,255,255,1) 0%, rgba(241,244,255,1) 100%)',
    boxShadow: '0 12px 28px rgba(87, 87, 176, 0.12)',
  },
};

const normalizeBootstrapFolderName = (value) => {
  const normalizedValue = String(value || '').trim().replace(/\\/g, '/');
  if (!normalizedValue) {
    return '';
  }
  return normalizedValue.split('/').filter(Boolean).pop() || '';
};

const formatBytes = (bytes = 0) => {
  if (!Number.isFinite(bytes) || bytes <= 0) {
    return '0 B';
  }
  const units = ['B', 'KB', 'MB', 'GB'];
  const exponent = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const value = bytes / Math.pow(1024, exponent);
  return `${value.toFixed(value >= 10 || exponent === 0 ? 0 : 1)} ${units[exponent]}`;
};

const parseBootstrapTimestamp = (value) => {
  if (Number.isFinite(Number(value)) && Number(value) > 0) {
    return Number(value);
  }
  const timestampText = String(value || '').trim();
  if (!timestampText) {
    return null;
  }
  const normalizedTimestamp = timestampText.includes('T')
    ? timestampText
    : timestampText.replace(' ', 'T');
  const timestampWithTimezone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(normalizedTimestamp)
    ? normalizedTimestamp
    : `${normalizedTimestamp}Z`;
  const parsed = Date.parse(timestampWithTimezone);
  return Number.isFinite(parsed) ? parsed : null;
};

const formatBootstrapElapsed = (elapsedMs, t) => {
  const elapsedSeconds = Math.max(0, Math.floor(Number(elapsedMs || 0) / 1000));
  if (elapsedSeconds < 60) {
    return t('addcase.bootstrap_elapsed_less_than_minute', 'less than a minute');
  }
  const elapsedMinutes = Math.floor(elapsedSeconds / 60);
  return t('addcase.bootstrap_elapsed_minutes', '{{count}} minutes', { count: elapsedMinutes });
};

const mergeDatasourceFiles = (currentFiles = [], nextFiles = []) => {
  const merged = [...currentFiles];
  const seen = new Set(
    currentFiles.map((file) => `${file?.name || ''}::${file?.size ?? 'unknown'}`)
  );
  const knownNames = new Set(currentFiles.map(file => file?.name));
  const unknownSizes = new Map(currentFiles.map((file, index) => [file?.name, index]).filter(([, index]) => currentFiles[index]?.size == null));

  nextFiles.forEach((file) => {
    const key = `${file?.name || ''}::${file?.size ?? 'unknown'}`;
    if (seen.has(key) || (file?.size == null && knownNames.has(file?.name))) {
      return;
    }
    seen.add(key);
    if (unknownSizes.has(file?.name)) {
      merged[unknownSizes.get(file.name)] = file;
      unknownSizes.delete(file.name);
      return;
    }
    knownNames.add(file?.name);
    merged.push(file);
  });

  return merged;
};

const resolveBootstrapTargetCount = (queuedCount, fallbackTarget = BOOTSTRAP_TARGET_COUNT) => {
  const safeFallbackTarget = Number.isFinite(Number(fallbackTarget))
    ? Math.max(1, Number(fallbackTarget))
    : BOOTSTRAP_TARGET_COUNT;
  const safeQueuedCount = Number.isFinite(Number(queuedCount))
    ? Math.max(0, Number(queuedCount))
    : 0;

  if (safeQueuedCount > 0) {
    return Math.min(safeFallbackTarget, safeQueuedCount);
  }

  return safeFallbackTarget;
};

const createProjectRequest = (payload = {}) => apiRequest('/api/v1/projects', {
  method: 'POST',
  body: JSON.stringify(payload),
});

const AddProject = ({ autoOpenTemplates = false, onboardingMode = false, onboardingCanStart = true, onboardingNotice = null, onboardingStorage = null }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { subscribeToMessages, sendMessage, serverInfo } = useWebSocket();
  const onboarding = onboardingMode || searchParams.get('onboarding') === 'true';
  const [initialDraft] = useState(() => readFirstResultDraft(onboarding ? onboardingStorage : null));
  const resumeCaseId = searchParams.get('resumeCaseId') || (onboarding ? initialDraft.caseId : null);
  const onboardingAccessRef = useRef(onboardingCanStart);
  onboardingAccessRef.current = onboardingCanStart;
  useEffect(() => {
    onboardingAccessRef.current = onboardingCanStart;
    return () => { onboardingAccessRef.current = false; };
  }, [onboardingCanStart]);
  const onboardingCompletedRef = useRef(false);
  const onboardingGoalEditedRef = useRef(initialDraft.hasSavedGoal);
  const onboardingRetryFilesRef = useRef([]);
  const [onboardingPendingFiles, setOnboardingPendingFiles] = useState([]);
  const [onboardingHasSource, setOnboardingHasSource] = useState(false);
  const [onboardingConfirmedFiles, setOnboardingConfirmedFiles] = useState(initialDraft.files);
  const [onboardingGenerationError, setOnboardingGenerationError] = useState('');
  const [onboardingConnectorsOpen, setOnboardingConnectorsOpen] = useState(false);
  const [onboardingSetupRequest, setOnboardingSetupRequest] = useState(null);
  const onboardingPreparingRequestRef = useRef(false);
  const [onboardingInvalidResume, setOnboardingInvalidResume] = useState(false);

  useEffect(() => {
    if (onboarding) sessionStorage.removeItem('dataunfold.saasWelcome');
  }, [onboarding]);

  const [status, setStatus] = useState('datasource');
  const [goal, setGoal] = useState(initialDraft.goal);
  const currentGoalRef = useRef(goal);
  currentGoalRef.current = goal;
  const [typedPlaceholder, setTypedPlaceholder] = useState('');
  const [updateMessage, setUpdateMessage] = useState('');
  const [serverVersion, setServerVersion] = useState(null);
  const [temporaryCaseId, setTemporaryCaseId] = useState(null);
  const [flowProjectTitle, setFlowProjectTitle] = useState('');
  const [isStartingFlowProject, setIsStartingFlowProject] = useState(false);
  const [flowCreatorError, setFlowCreatorError] = useState('');
  const [flowCreatorNotice, setFlowCreatorNotice] = useState('');
  const [pendingFlowInteraction, setPendingFlowInteraction] = useState('');
  const [isFlowTitleDialogOpen, setIsFlowTitleDialogOpen] = useState(false);
  const [focusFlowTitleSignal, setFocusFlowTitleSignal] = useState(0);
  const [pendingCaseId, setPendingCaseId] = useState(null);
  const [autoOpenConnector, setAutoOpenConnector] = useState('');
  const [connectorWizardRequest, setConnectorWizardRequest] = useState(null);
  const [isPreparingTemporaryProject, setIsPreparingTemporaryProject] = useState(false);
  const [isUploadingDatasource, setIsUploadingDatasource] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadedDatasourceFiles, setUploadedDatasourceFiles] = useState(initialDraft.files);
  const [datasourceUploadError, setDatasourceUploadError] = useState('');
  const [bootstrapError, setBootstrapError] = useState('');
  const [bootstrapRepairPrompt, setBootstrapRepairPrompt] = useState(false);
  const [bootstrapTimedOut, setBootstrapTimedOut] = useState(false);
  const [bootstrapClock, setBootstrapClock] = useState(Date.now());
  const [isMixedConsentOpen, setIsMixedConsentOpen] = useState(false);
  const skipInitialAiSetup = false;
  const [bootstrapReady, setBootstrapReady] = useState(false);
  const [sampleItems, setSampleItems] = useState([]);
  const [sampleReadError, setSampleReadError] = useState(false);
  const bootstrapSampleActiveRef = useRef(false);
  const bootstrapSampleAcceptedRef = useRef(false);
  const bootstrapSampleRunRef = useRef(0);

  useEffect(() => {
    setSampleItems([]);
    setSampleReadError(false);
  }, [temporaryCaseId]);

  useEffect(() => {
    if (status !== 'bootstrap' || !temporaryCaseId) return undefined;
    let cancelled = false;
    let timer;
    const sampleRun = bootstrapSampleRunRef.current;
    const refresh = async () => {
      let sampleReady = false;
      try {
        const result = await apiRequest(`/api/v1/projects/${temporaryCaseId}/creation-sample`);
        if (cancelled || sampleRun !== bootstrapSampleRunRef.current) return;
        const items = Array.isArray(result.items) ? result.items : [];
        sampleReady = items.length > 0;
        setSampleItems(items);
        setSampleReadError(false);
        if (items.length) {
          setBootstrapReady(true);
          setBootstrapError('');
          setBootstrapTimedOut(false);
          setBootstrapRepairPrompt(false);
          setBootstrapProgress((current) => ({
            ...current, phase: 'ready', usableCount: items.length,
            processedCount: Math.max(current.processedCount, items.length),
          }));
        }
      } catch {
        if (!cancelled && sampleRun === bootstrapSampleRunRef.current) {
          setSampleReadError(true);
        }
      } finally {
        if (!cancelled && sampleRun === bootstrapSampleRunRef.current && !(onboarding && sampleReady)) {
          timer = setTimeout(refresh, 5000);
        }
      }
    };
    refresh();
    return () => { cancelled = true; clearTimeout(timer); };
  }, [onboarding, status, temporaryCaseId]);
  const [templateSelectionOpen, setTemplateSelectionOpen] = useState(
    () => searchParams.get('templateBrowser') === 'true'
  );

  useEffect(() => {
    if (autoOpenTemplates && location.pathname === '/' && !resumeCaseId) {
      setTemplateSelectionOpen(true);
    }
  }, [autoOpenTemplates, location.pathname, resumeCaseId]);
  const [bootstrapProgress, setBootstrapProgress] = useState({
    phase: 'waiting',
    processedCount: 0,
    queuedCount: 0,
    usableCount: 0,
    failedCount: 0,
    targetCount: BOOTSTRAP_TARGET_COUNT,
    message: '',
    latestItem: null,
    sourceType: '',
    startedAt: null,
    lastUpdatedAt: null,
  });

  const shouldAwaitCreationUpdatesRef = useRef(false);
  const temporaryProjectPromiseRef = useRef(null);
  const flowProjectPromiseRef = useRef(null);
  const lastResumeCaseIdRef = useRef(resumeCaseId);
  const bootstrapConnectorContextRef = useRef(null);
  // Force a fresh dialog instance whenever we reopen the connector after a failure.
  const bootstrapConnectorWizardRevisionRef = useRef(0);
  const bootstrapRepairAttemptCountRef = useRef(0);
  const bootstrapRepairInFlightRef = useRef(false);

  const resetBootstrapRepairState = useCallback(() => {
    bootstrapRepairAttemptCountRef.current = 0;
    bootstrapRepairInFlightRef.current = false;
    setBootstrapRepairPrompt(false);
  }, []);

  const resetBootstrapSampleState = useCallback((active = false) => {
    bootstrapSampleActiveRef.current = active;
    bootstrapSampleRunRef.current += 1;
    bootstrapSampleAcceptedRef.current = false;
    setBootstrapReady(false);
    setSampleItems([]);
    setSampleReadError(false);
  }, []);

  const openBootstrapConnectorWizard = useCallback((context = null) => {
    const resolvedContext = context || bootstrapConnectorContextRef.current;
    if (!resolvedContext) {
      return false;
    }

    bootstrapConnectorWizardRevisionRef.current += 1;
    setConnectorWizardRequest({
      ...resolvedContext,
      wizardKey: bootstrapConnectorWizardRevisionRef.current,
    });
    return true;
  }, []);

  const rotatingPlaceholders = useMemo(
    () => [
      t('addcase.input_placeholder'),
      t('addcase.input_placeholder_accountant'),
      t('addcase.input_placeholder_sales'),
      t('addcase.input_placeholder_hr'),
    ],
    [t]
  );
  const maxRecordingDurationSeconds = useMemo(() => {
    const configured = Number(serverInfo?.maxAudioRecordingLengthSeconds);
    if (Number.isFinite(configured) && configured > 0) {
      return Math.floor(configured);
    }
    return undefined;
  }, [serverInfo]);
  const {
    canUseDisplayAudio,
    clearPendingAudio: clearPendingAudioReview,
    isAudioReviewOpen,
    isRecording: isRecordingAudio,
    pendingAudio: pendingAudioReview,
    recordingElapsed,
    setIsAudioReviewOpen,
    startRecording: startAudioRecording,
    stopRecording: stopAudioRecording,
    waveformHistory,
  } = useAudioRecorder({
    isMobile,
    maxRecordingDurationSeconds,
    onError: (errorKey) => {
      if (errorKey === 'system_audio_unavailable') {
        setDatasourceUploadError(
          t(
            'addcase.system_audio_unavailable',
            'System audio capture requires screen sharing; recording microphone only.'
          )
        );
        return;
      }
      if (errorKey === 'audio_recording_empty') {
        setDatasourceUploadError(t('addcase.audio_recording_empty', 'No audio captured.'));
        return;
      }
      if (errorKey === 'audio_recording_unsupported') {
        setDatasourceUploadError(t('addcase.audio_recording_unsupported', 'Audio recording is not supported in this browser.'));
        return;
      }
      setDatasourceUploadError(t('addcase.audio_recording_error', 'Recording failed.'));
    },
  });

  useEffect(() => {
    if (!resumeCaseId || !rotatingPlaceholders.length) {
      return undefined;
    }

    let timeoutId;
    let exampleIndex = 0;
    let characterIndex = 0;
    const typeSpeedMs = 38;
    const deleteSpeedMs = 20;
    const pauseAfterTypingMs = 1300;
    const pauseBeforeTypingMs = 250;

    const startTyping = () => {
      const fullText = rotatingPlaceholders[exampleIndex] || '';
      if (characterIndex < fullText.length) {
        characterIndex += 1;
        setTypedPlaceholder(fullText.slice(0, characterIndex));
        timeoutId = setTimeout(startTyping, typeSpeedMs);
        return;
      }
      timeoutId = setTimeout(startDeleting, pauseAfterTypingMs);
    };

    const startDeleting = () => {
      const fullText = rotatingPlaceholders[exampleIndex] || '';
      if (characterIndex > 0) {
        characterIndex -= 1;
        setTypedPlaceholder(fullText.slice(0, characterIndex));
        timeoutId = setTimeout(startDeleting, deleteSpeedMs);
        return;
      }
      exampleIndex = (exampleIndex + 1) % rotatingPlaceholders.length;
      timeoutId = setTimeout(startTyping, pauseBeforeTypingMs);
    };

    setTypedPlaceholder('');
    timeoutId = setTimeout(startTyping, pauseBeforeTypingMs);
    return () => clearTimeout(timeoutId);
  }, [resumeCaseId, rotatingPlaceholders]);

  useEffect(() => {
    let mounted = true;
    fetch('/api/server_status')
      .then((res) => (res.ok ? res.json() : Promise.reject()))
      .then((data) => {
        if (mounted && data?.server_version) {
          setServerVersion(data.server_version);
        }
      })
      .catch(() => {});

    return () => {
      mounted = false;
    };
  }, []);

  const beginTemporaryProjectCreation = useCallback(async () => {
    if (onboarding && !onboardingAccessRef.current) {
      throw new Error(t('first_result.activation_help'));
    }
    shouldAwaitCreationUpdatesRef.current = false;
    setUpdateMessage('');
    setDatasourceUploadError('');
    setIsPreparingTemporaryProject(true);

    try {
      const payload = await createProjectRequest({
        folder: '',
        title: TEMP_PROJECT_TITLE,
        description: TEMP_PROJECT_DESCRIPTION,
        goal: '',
        customFields: [],
        attachments: [],
        defer_ai_processing: true,
        skip_initial_ai_setup: skipInitialAiSetup,
        ...(onboarding ? {
          projectModelProvider: 'dataunfold',
          projectModelOverrides: {
            textModelProvider: 'dataunfold',
            visionModelProvider: 'dataunfold',
            audioModelProvider: 'dataunfold',
          },
        } : {}),
      });

      if (payload?.status !== 'success' || !payload?.id) {
        throw new Error(payload?.error || payload?.message || 'Unable to create temporary project.');
      }

      setIsPreparingTemporaryProject(false);
      setTemporaryCaseId(payload.id);
      return payload.id;
    } catch (error) {
      setIsPreparingTemporaryProject(false);
      setAutoOpenConnector('');
      setIsUploadingDatasource(false);
      setUploadProgress(0);
      setStatus('datasource');
      throw error;
    }
  }, [onboarding, skipInitialAiSetup, t]);

  const ensureTemporaryProject = useCallback(() => {
    if (temporaryCaseId) {
      return Promise.resolve(temporaryCaseId);
    }
    if (temporaryProjectPromiseRef.current) {
      return temporaryProjectPromiseRef.current;
    }

    temporaryProjectPromiseRef.current = beginTemporaryProjectCreation()
      .finally(() => {
        temporaryProjectPromiseRef.current = null;
      });
    return temporaryProjectPromiseRef.current;
  }, [beginTemporaryProjectCreation, temporaryCaseId]);

  const saveFlowProject = useCallback((projectTitle, interactionType = '') => {
    if (flowProjectPromiseRef.current) {
      return flowProjectPromiseRef.current;
    }

    setIsStartingFlowProject(true);
    setFlowCreatorError('');
    flowProjectPromiseRef.current = createProjectRequest({
      folder: '',
      title: projectTitle,
      description: '',
      goal: '',
      customFields: [],
      attachments: [],
      skip_initial_ai_setup: true,
    })
      .then((payload) => {
        if (payload?.status !== 'success' || !payload?.id) {
          throw new Error(payload?.error || payload?.message || 'Unable to create the project.');
        }
        saveProjectViewForSession(payload.id, FLOWS_PROJECT_VIEW);
        notifyProjectCreated({
          ...(payload.project || {}),
          id: payload.id,
          title: payload.project?.title || projectTitle,
          created_at: payload.project?.created_at || new Date().toISOString(),
          last_visit: payload.project?.last_visit || new Date().toISOString(),
          hasNewEvents: payload.project?.hasNewEvents || 0,
        });
        const nextParams = new URLSearchParams({ view: 'flows' });
        if (['action', 'connector', 'flow'].includes(interactionType)) {
          nextParams.set('flowInteraction', interactionType);
        }
        navigate(`/case/${payload.id}?${nextParams.toString()}`, { replace: true });
        return payload.id;
      })
      .catch((error) => {
        setFlowCreatorError(error?.message || t('addcase.flow_creator_error', 'Unable to create the project.'));
        throw error;
      })
      .finally(() => {
        flowProjectPromiseRef.current = null;
        setIsStartingFlowProject(false);
      });
    return flowProjectPromiseRef.current;
  }, [navigate, t]);

  const handleSaveFlowProject = useCallback(() => {
    if (flowProjectPromiseRef.current) return;
    const projectTitle = String(flowProjectTitle || '').trim();
    if (!projectTitle) {
      setFlowCreatorError(isFlowTitleDialogOpen
        ? t('addcase.flow_creator_title_required', 'Enter a project title.')
        : '');
      setFlowCreatorNotice('');
      setIsFlowTitleDialogOpen(true);
      return;
    }
    saveFlowProject(projectTitle, pendingFlowInteraction).catch(() => {});
  }, [flowProjectTitle, isFlowTitleDialogOpen, pendingFlowInteraction, saveFlowProject, t]);

  const handleStartFlowCreator = useCallback((interactionType = 'action') => {
    if (flowProjectPromiseRef.current) return;
    const projectTitle = String(flowProjectTitle || '').trim();
    if (!projectTitle) {
      setPendingFlowInteraction(interactionType);
      setFlowCreatorError('');
      setFlowCreatorNotice('');
      setIsFlowTitleDialogOpen(true);
      return;
    }
    setPendingFlowInteraction('');
    setFlowCreatorNotice('');
    saveFlowProject(projectTitle, interactionType).catch(() => {});
  }, [flowProjectTitle, saveFlowProject]);

  const handleCloseFlowTitleDialog = useCallback(() => {
    if (flowProjectPromiseRef.current) return;
    setIsFlowTitleDialogOpen(false);
    setPendingFlowInteraction('');
    setFlowCreatorError('');
  }, []);

  const handleUseFlowTemplate = useCallback(() => {
    setPendingFlowInteraction('');
    setFlowCreatorNotice('');
    setFlowCreatorError('');
    setTemplateSelectionOpen(true);
  }, []);

  const handleStartBlankProject = useCallback(() => {
    setPendingFlowInteraction('');
    setFlowCreatorNotice('');
    setFlowCreatorError('');
    setFocusFlowTitleSignal((current) => current + 1);
  }, []);

  const handleDatasourceUploadResult = useCallback((data = {}, sourceFiles = []) => {
    const queued = Array.isArray(data?.queued)
      ? data.queued
      : (Array.isArray(data?.files) ? data.files : []);
    const failed = Array.isArray(data?.failed) ? data.failed : [];
    const sizeByName = new Map(
      (Array.isArray(sourceFiles) ? sourceFiles : [])
        .filter(Boolean)
        .map((file) => [file.name, file.size ?? null])
    );
    const queuedEntries = queued
      .map((entry) => {
        const name = typeof entry === 'string' ? entry : entry?.filename || entry?.name || '';
        return name ? { name, size: entry?.size ?? sizeByName.get(name) ?? null } : null;
      })
      .filter(Boolean);
    setIsUploadingDatasource(false);
    setUploadProgress(100);
    if (queuedEntries.length > 0) {
      if (onboarding) {
        setOnboardingHasSource(true);
        setOnboardingConfirmedFiles((current) => mergeDatasourceFiles(current, queuedEntries));
        const failedNames = new Set(failed.map(file => typeof file === 'string' ? file : file?.filename || file?.name));
        onboardingRetryFilesRef.current = onboardingRetryFilesRef.current.filter(file => failedNames.has(file.name));
      }
      setUploadedDatasourceFiles((current) => mergeDatasourceFiles(current, queuedEntries));
      setDatasourceUploadError(onboarding && failed.length ? t('first_result.source_error') : '');
      setStatus('bootstrap');
      setBootstrapTimedOut(false);
      setBootstrapProgress((current) => ({
        ...current,
        phase: 'ingesting',
        queuedCount: Math.max(current.queuedCount || 0, queuedEntries.length),
        targetCount: resolveBootstrapTargetCount(
          Math.max(current.queuedCount || 0, queuedEntries.length),
          current.targetCount
        ),
        message: failed.length > 0 && data?.message
          ? data.message
          : (
            current.sourceType === 'connector'
              ? current.message
              : t(
                'addcase.bootstrap_ingesting_message',
                'Uploading is complete. We are ingesting the first items to prepare your project question...'
              )
          ),
        startedAt: current.startedAt || Date.now(),
        lastUpdatedAt: Date.now(),
      }));
      return true;
    } else if (onboarding || failed.length > 0 || data?.status === 'error') {
      setDatasourceUploadError(
        data?.message || t('addcase.datasource_upload_error', 'Unable to upload the dropped files.')
      );
      setStatus('datasource');
    }
    return false;
  }, [onboarding, t]);

  const uploadDatasourceFiles = useCallback(async (targetCaseId, files) => {
    const fileList = Array.isArray(files) ? files.filter(Boolean) : [];
    if (!targetCaseId || fileList.length === 0) {
      return false;
    }
    if (onboarding && !onboardingAccessRef.current) return false;

    setDatasourceUploadError('');
    setIsUploadingDatasource(true);
    setUploadProgress(5);

    try {
      const formData = new FormData();
      formData.append('caseid', String(targetCaseId));
      fileList.forEach((file) => {
        formData.append('files', file, file.name);
      });
      setUploadProgress(75);
      const payload = await apiRequest('/api/v1/projects/upload', {
        method: 'POST',
        body: formData,
      });
      return handleDatasourceUploadResult(payload, fileList);
    } catch (error) {
      console.error('[AddProject] Failed to upload datasource files:', error);
      setIsUploadingDatasource(false);
      setUploadProgress(0);
      setDatasourceUploadError(
        error?.message || t('addcase.datasource_upload_error', 'Unable to upload the dropped files.')
      );
      setStatus('datasource');
      return false;
    }
  }, [handleDatasourceUploadResult, onboarding, t]);

  const beginBootstrapForDatasource = useCallback((queuedCount, sourceType, message) => {
    bootstrapConnectorContextRef.current = null;
    resetBootstrapRepairState();
    bootstrapConnectorWizardRevisionRef.current = 0;
    setConnectorWizardRequest(null);
    setStatus('bootstrap');
    resetBootstrapSampleState(true);
    setBootstrapError('');
    setBootstrapTimedOut(false);
    const startedAt = Date.now();
    setBootstrapProgress({
      phase: sourceType === 'connector' ? 'ingesting' : 'uploading',
      processedCount: 0,
      queuedCount,
      usableCount: 0,
      failedCount: 0,
      targetCount: resolveBootstrapTargetCount(queuedCount),
      message,
      latestItem: null,
      sourceType,
      startedAt,
      lastUpdatedAt: startedAt,
    });
  }, [resetBootstrapRepairState, resetBootstrapSampleState]);

  const handleDatasourceDrop = useCallback((acceptedFiles) => {
    const fileList = Array.isArray(acceptedFiles) ? acceptedFiles.filter(Boolean) : [];
    if (fileList.length === 0) {
      return;
    }
    if (onboarding) onboardingRetryFilesRef.current = fileList;

    beginBootstrapForDatasource(
      fileList.length,
      'upload',
      t(
        'addcase.bootstrap_uploading_message',
        'Uploading your datasource and preparing the first items for project setup...'
      )
    );

    ensureTemporaryProject()
      .then((createdCaseId) => {
        uploadDatasourceFiles(createdCaseId, fileList);
      })
      .catch(() => {
        setDatasourceUploadError(t('addcase.datasource_upload_error', 'Unable to upload the dropped files.'));
      });
  }, [beginBootstrapForDatasource, ensureTemporaryProject, onboarding, t, uploadDatasourceFiles]);

  // Files can be chosen during activation, but no request starts before access is ready.
  useEffect(() => {
    if (!onboarding || !onboardingCanStart || !onboardingPendingFiles.length || isUploadingDatasource || isPreparingTemporaryProject || status === 'updating') return;
    const files = onboardingPendingFiles;
    setOnboardingPendingFiles([]);
    handleDatasourceDrop(files);
  }, [handleDatasourceDrop, isPreparingTemporaryProject, isUploadingDatasource, onboarding, onboardingCanStart, onboardingPendingFiles, status]);

  const handleDatasourceBeforeOpen = useCallback(async () => true, []);

  const updateBootstrapConnectorContext = useCallback((details = {}, options = {}) => {
    const previousContext = bootstrapConnectorContextRef.current || {};
    const resolvedCaseId = Number.isFinite(Number(details?.caseid))
      ? Number(details.caseid)
      : (Number.isFinite(Number(previousContext.caseid)) ? Number(previousContext.caseid) : null);
    const resolvedDatasourceId = Number.isFinite(Number(details?.datasourceId))
      ? Number(details.datasourceId)
      : (Number.isFinite(Number(previousContext.datasourceId)) ? Number(previousContext.datasourceId) : null);
    const nextContext = {
      ...previousContext,
      ...details,
      caseid: resolvedCaseId,
      datasourceId: resolvedDatasourceId,
      type: String(details?.type || previousContext.type || '').trim(),
      title: String(details?.title || previousContext.title || details?.type || '').trim(),
      folderName: normalizeBootstrapFolderName(
        details?.folderName
        || details?.created?.folder_name
        || details?.created?.folder
        || previousContext.folderName
        || ''
      ),
      manifest: details?.manifest || previousContext.manifest || null,
      created: details?.created || previousContext.created || null,
      plan: details?.plan || previousContext.plan || null,
    };
    bootstrapConnectorContextRef.current = nextContext;
    if (options.resetRepairState !== false) {
      resetBootstrapRepairState();
    }
    return nextContext;
  }, [resetBootstrapRepairState]);

  const handleDatasourceConfigured = useCallback((details = {}) => {
    if (onboarding) {
      setOnboardingHasSource(true);
      setOnboardingConnectorsOpen(false);
      setOnboardingSetupRequest(null);
    }
    setAutoOpenConnector('');
    setBootstrapError('');
    const resolvedContext = updateBootstrapConnectorContext(details);
    const resolvedCaseId = resolvedContext?.caseid || details?.caseid || temporaryCaseId;
    if (resolvedCaseId) {
      setTemporaryCaseId(resolvedCaseId);
    }
    setStatus('bootstrap');
    resetBootstrapSampleState(true);
    setBootstrapTimedOut(false);
    const startedAt = Date.now();
    setBootstrapProgress({
      phase: 'ingesting',
      processedCount: 0,
      queuedCount: 0,
      usableCount: 0,
      failedCount: 0,
      targetCount: BOOTSTRAP_TARGET_COUNT,
      message: t(
        'addcase.bootstrap_connector_message',
        'Preparing the first connector events for project setup...'
      ),
      latestItem: null,
      sourceType: resolvedContext?.type || details?.type || 'connector',
      startedAt,
      lastUpdatedAt: startedAt,
    });

    const normalizedDatasourceId = Number(resolvedContext?.datasourceId ?? details?.datasourceId);
    if (resolvedCaseId && Number.isFinite(normalizedDatasourceId)) {
      sendMessage({
        command: 'forceRunDatasource',
        caseid: resolvedCaseId,
        datasource_id: normalizedDatasourceId,
      });
    }
  }, [onboarding, resetBootstrapSampleState, sendMessage, t, temporaryCaseId, updateBootstrapConnectorContext]);

  const handleUploadRecordedAudio = useCallback(() => {
    if (!pendingAudioReview?.file) {
      return;
    }

    beginBootstrapForDatasource(
      1,
      'audio',
      t(
        'addcase.bootstrap_audio_message',
        'Uploading your recording and preparing the first items for project setup...'
      )
    );

    const audioFile = pendingAudioReview.file;
    setIsAudioReviewOpen(false);

    ensureTemporaryProject()
      .then(async (createdCaseId) => {
        const uploaded = await uploadDatasourceFiles(createdCaseId, [audioFile]);
        if (uploaded) {
          clearPendingAudioReview();
        } else {
          setIsAudioReviewOpen(true);
        }
      })
      .catch(() => {
        setDatasourceUploadError(t('addcase.datasource_upload_error', 'Unable to upload the dropped files.'));
        setStatus('datasource');
        setIsAudioReviewOpen(true);
      });
  }, [
    beginBootstrapForDatasource,
    clearPendingAudioReview,
    ensureTemporaryProject,
    pendingAudioReview,
    t,
    uploadDatasourceFiles,
  ]);

  const handleFinalizeProject = useCallback(() => {
    if (!temporaryCaseId || !goal.trim()) {
      return;
    }
    if (onboarding && (!onboardingAccessRef.current || !bootstrapReady || isUploadingDatasource || isPreparingTemporaryProject || status === 'updating')) return;
    setOnboardingGenerationError('');

    shouldAwaitCreationUpdatesRef.current = true;
    startCaseCreation();

    try {
      sendMessage({
        command: 'FinalizeProjectCreation',
        caseid: temporaryCaseId,
        goal: goal.trim(),
        skip_initial_ai_setup: skipInitialAiSetup,
      });
      setStatus('updating');
    } catch {
      shouldAwaitCreationUpdatesRef.current = false;
      clearCaseCreation();
      setOnboardingGenerationError(t('first_result.generation_error'));
    }
  }, [bootstrapReady, goal, isPreparingTemporaryProject, isUploadingDatasource, onboarding, sendMessage, skipInitialAiSetup, status, t, temporaryCaseId]);

  const handlePrepareOnboardingRequest = async () => {
    if (!onboardingAccessRef.current || !goal.trim() || onboardingPreparingRequestRef.current
      || isUploadingDatasource || isPreparingTemporaryProject || status === 'bootstrap' || status === 'updating'
      || datasourceUploadError || bootstrapError || (resumeCaseId && !temporaryCaseId)) return;
    onboardingPreparingRequestRef.current = true;
    setOnboardingGenerationError('');
    try {
      await ensureTemporaryProject();
      if (!onboardingAccessRef.current) return;
      const currentGoal = currentGoalRef.current.trim();
      if (!currentGoal) return;
      setOnboardingSetupRequest({ id: Date.now(), request: currentGoal });
      setOnboardingConnectorsOpen(true);
    } catch {
      setOnboardingGenerationError(t('first_result.generation_error'));
    } finally {
      onboardingPreparingRequestRef.current = false;
    }
  };

  const handleOnboardingGoalChange = value => {
    onboardingGoalEditedRef.current = true;
    setGoal(value.slice(0, FIRST_RESULT_GOAL_MAX_LENGTH));
  };

  const applyResumeCaseState = useCallback((payload = {}, options = {}) => {
    const caseData = payload?.case;
    if (!caseData) {
      return false;
    }

    const creationWizardState = payload?.creationWizardState || {};
    const resumedCaseId = caseData.id || resumeCaseId;
    const resumedStep = String(creationWizardState.step || '').toLowerCase();
    const resumedGoal = String(caseData.goal || '');
    const resumedBootstrapStatus = String(creationWizardState.bootstrapStatus || '').toLowerCase();
    const resumedBootstrapError = String(
      creationWizardState.bootstrapError || creationWizardState.message || ''
    );
    const resumedTargetCount = resolveBootstrapTargetCount(
      creationWizardState.bootstrapFileCount,
      creationWizardState.bootstrapTargetCount
    );
    const resumedProcessedCount = Number(creationWizardState.bootstrapProcessedCount) || 0;
    const resumedQueuedCount = Number(creationWizardState.bootstrapQueuedCount)
      || creationWizardState.bootstrapFileCount
      || 0;
    const resumedStartedAt = parseBootstrapTimestamp(creationWizardState.bootstrapCreatedAt);
    const resumedUpdatedAt = parseBootstrapTimestamp(creationWizardState.bootstrapUpdatedAt);

    // Background state must never replace an in-progress request or collapse its preview.
    if (onboarding && options.preserveBootstrapContext) {
      if (shouldAwaitCreationUpdatesRef.current) return true;
      if (resumedStep === 'question' || resumedBootstrapStatus === 'ready') setBootstrapReady(true);
      if (resumedBootstrapStatus === 'error' && !bootstrapReady) setBootstrapError(resumedBootstrapError);
      return Boolean(resumedStep);
    }

    setTemporaryCaseId(resumedCaseId);
    if (!onboarding || !onboardingGoalEditedRef.current) setGoal(resumedGoal);
    if (onboarding) setOnboardingHasSource(resumedStep !== 'datasource');
    setAutoOpenConnector('');
    setDatasourceUploadError('');
    setIsPreparingTemporaryProject(false);
    setIsUploadingDatasource(false);
    setUploadProgress(0);
    if (!options.preserveBootstrapContext) {
      bootstrapConnectorContextRef.current = null;
      resetBootstrapRepairState();
      bootstrapConnectorWizardRevisionRef.current = 0;
      setConnectorWizardRequest(null);
    }
    shouldAwaitCreationUpdatesRef.current = resumedStep === 'updating';
    bootstrapSampleActiveRef.current = false;

    if (resumedStep === 'datasource') {
      bootstrapSampleRunRef.current += 1;
      bootstrapSampleAcceptedRef.current = false;
      setSampleItems([]);
      setSampleReadError(false);
      setPendingCaseId(null);
      setUpdateMessage('');
      setBootstrapReady(false);
      setBootstrapTimedOut(false);
      setStatus('datasource');
      return true;
    }

    if (resumedStep === 'bootstrap') {
      bootstrapSampleActiveRef.current = true;
      bootstrapSampleRunRef.current += 1;
      bootstrapSampleAcceptedRef.current = false;
      setSampleItems([]);
      setSampleReadError(false);
      setPendingCaseId(null);
      setUpdateMessage('');
      setBootstrapReady(false);
      const hasResumedProgress = resumedBootstrapStatus === 'ready' || resumedProcessedCount > 0;
      setBootstrapTimedOut((currentTimedOut) => (
        currentTimedOut && !hasResumedProgress && resumedBootstrapStatus !== 'error'
          ? true
          : false
      ));
      setBootstrapError((currentError) => (
        resumedBootstrapStatus === 'error'
          ? resumedBootstrapError
          : (hasResumedProgress ? '' : currentError)
      ));
      setDatasourceUploadError((currentError) => (
        resumedBootstrapStatus === 'error'
          ? resumedBootstrapError
          : (hasResumedProgress ? '' : currentError)
      ));
      setBootstrapProgress((current) => ({
        ...current,
        phase: resumedBootstrapStatus === 'error'
          ? 'error'
          : resumedBootstrapStatus === 'ready'
            ? 'ready'
            : resumedProcessedCount > 0
              ? 'scanning'
              : current.phase === 'ready' ? 'ready' : 'waiting',
        processedCount: resumedProcessedCount,
        queuedCount: resumedQueuedCount || current.queuedCount || 0,
        usableCount: Number(creationWizardState.bootstrapUsableCount) || 0,
        failedCount: Number(creationWizardState.bootstrapFailedCount) || 0,
        targetCount: resumedTargetCount,
        message: resumedBootstrapError || current.message || t(
            'addcase.bootstrap_waiting_message',
            'Waiting for the first datasource items to be ready...'
          ),
        latestItem: null,
        startedAt: resumedStartedAt || current.startedAt || Date.now(),
        lastUpdatedAt: resumedUpdatedAt || current.lastUpdatedAt,
      }));
      setStatus('bootstrap');
      return true;
    }

    if (resumedStep === 'question') {
      bootstrapSampleActiveRef.current = true;
      bootstrapSampleRunRef.current += 1;
      bootstrapSampleAcceptedRef.current = true;
      setPendingCaseId(null);
      setUpdateMessage('');
      setBootstrapReady(true);
      setBootstrapTimedOut(false);
      setStatus('question');
      return true;
    }

    if (resumedStep === 'updating') {
      bootstrapSampleRunRef.current += 1;
      bootstrapSampleAcceptedRef.current = true;
      setPendingCaseId(resumedCaseId);
      setBootstrapReady(true);
      setBootstrapTimedOut(false);
      setUpdateMessage(
        creationWizardState.message || t('addcase.creating_project', 'Creating your project...')
      );
      setStatus('updating');
      return true;
    }

    return false;
  }, [bootstrapReady, onboarding, resetBootstrapRepairState, resumeCaseId, t]);

  const finalizeBootstrapFailure = useCallback((errorMessage, options = {}) => {
    const resolvedErrorMessage = errorMessage || t('addcase.datasource_upload_error', 'Unable to process this datasource.');
    const shouldReopenConnectorWizard = Boolean(options?.reopenConnectorWizard && bootstrapConnectorContextRef.current);

    bootstrapRepairInFlightRef.current = false;
    setIsPreparingTemporaryProject(false);
    setIsUploadingDatasource(false);
    setUploadProgress(0);
    setBootstrapReady(false);
    setBootstrapTimedOut(false);
    setBootstrapError(resolvedErrorMessage);
    setDatasourceUploadError(resolvedErrorMessage);
    setBootstrapProgress((current) => ({
      ...current,
      phase: shouldReopenConnectorWizard ? 'repair_failed' : current.phase,
      message: resolvedErrorMessage,
    }));
    setStatus('bootstrap');
    if (shouldReopenConnectorWizard) {
      openBootstrapConnectorWizard(bootstrapConnectorContextRef.current);
    }
    clearCaseCreation();
  }, [openBootstrapConnectorWizard, t]);

  const attemptBootstrapRepair = useCallback((errorMessage) => {
    const connectorContext = bootstrapConnectorContextRef.current;
    const currentAttemptCount = Number(bootstrapRepairAttemptCountRef.current) || 0;
    if (
      !connectorContext
      || bootstrapRepairInFlightRef.current
      || currentAttemptCount >= BOOTSTRAP_REPAIR_MAX_ATTEMPTS
    ) {
      return false;
    }

    const resolvedCaseId = Number.isFinite(Number(connectorContext.caseid))
      ? Number(connectorContext.caseid)
      : (Number.isFinite(Number(temporaryCaseId)) ? Number(temporaryCaseId) : null);
    const resolvedDatasourceId = Number.isFinite(Number(connectorContext.datasourceId))
      ? Number(connectorContext.datasourceId)
      : null;
    if (!resolvedCaseId || !resolvedDatasourceId) {
      return false;
    }

    if (typeof sendMessage !== 'function') {
      return false;
    }

    bootstrapRepairAttemptCountRef.current = currentAttemptCount + 1;
    bootstrapRepairInFlightRef.current = true;
    setBootstrapRepairPrompt(false);
    setBootstrapError('');
    setBootstrapTimedOut(false);
    setDatasourceUploadError('');
    setBootstrapProgress((current) => ({
      ...current,
      phase: 'repairing',
      message: t(
        'addcase.bootstrap_repairing_message',
        'The connector failed to bootstrap. Attempting an automatic repair...'
      ),
    }));

    try {
      sendMessage({
        command: 'repairGeneratedPlugin',
        caseid: resolvedCaseId,
        datasource_id: resolvedDatasourceId,
        folderName: connectorContext.folderName,
        pluginType: connectorContext.type,
        type: connectorContext.type,
        creatorType: 'connector',
        error: String(errorMessage || '').trim(),
        message: String(errorMessage || '').trim(),
      });
      return true;
    } catch {
      bootstrapRepairAttemptCountRef.current = currentAttemptCount;
      bootstrapRepairInFlightRef.current = false;
      return false;
    }
  }, [sendMessage, t, temporaryCaseId]);

  const retryBootstrapAfterRepair = useCallback((repairPayload = {}) => {
    bootstrapRepairInFlightRef.current = false;

    if (String(repairPayload?.status || '').toLowerCase() !== 'success') {
      finalizeBootstrapFailure(
        repairPayload?.code === 'project_plugin_repair_failed'
          ? t('addcase.bootstrap_repair_access_error')
          : repairPayload?.message || t('addcase.bootstrap_connector_error', 'Connector ingestion failed.'),
        { reopenConnectorWizard: true }
      );
      return;
    }

    const repairedPlugin = repairPayload?.plugin && typeof repairPayload.plugin === 'object'
      ? repairPayload.plugin
      : {};
    const updatedContext = updateBootstrapConnectorContext({
      folderName: repairPayload?.folderName || repairedPlugin.folderName || bootstrapConnectorContextRef.current?.folderName || '',
      type: repairedPlugin.type || bootstrapConnectorContextRef.current?.type || '',
      title: repairedPlugin.name || bootstrapConnectorContextRef.current?.title || repairedPlugin.type || '',
    }, { resetRepairState: false });
    const resolvedCaseId = Number.isFinite(Number(updatedContext.caseid))
      ? Number(updatedContext.caseid)
      : (Number.isFinite(Number(temporaryCaseId)) ? Number(temporaryCaseId) : null);
    const resolvedDatasourceId = Number.isFinite(Number(updatedContext.datasourceId))
      ? Number(updatedContext.datasourceId)
      : null;

    if (!resolvedCaseId || !resolvedDatasourceId) {
      finalizeBootstrapFailure(
        t(
          'addcase.bootstrap_retry_unavailable',
          'The connector was repaired, but bootstrap could not be retried automatically.'
        ),
        { reopenConnectorWizard: true }
      );
      return;
    }

    setBootstrapError('');
    setBootstrapTimedOut(false);
    setDatasourceUploadError('');
    setBootstrapRepairPrompt(false);
    setBootstrapProgress((current) => ({
      ...current,
      phase: 'restarting',
      message: t(
        'addcase.bootstrap_restart_message',
        'The connector was repaired. Restarting the bootstrap now...'
      ),
    }));

    if (repairPayload?.relaunchScheduled) {
      return;
    }

    if (typeof sendMessage === 'function') {
      sendMessage({
        command: 'forceRunDatasource',
        caseid: resolvedCaseId,
        datasource_id: resolvedDatasourceId,
      });
      return;
    }

    finalizeBootstrapFailure(
      t(
        'addcase.bootstrap_retry_unavailable',
        'The connector was repaired, but bootstrap could not be retried automatically.'
      ),
      { reopenConnectorWizard: true }
    );
  }, [finalizeBootstrapFailure, sendMessage, t, temporaryCaseId, updateBootstrapConnectorContext]);

  const returnToDatasource = useCallback(() => {
    bootstrapConnectorContextRef.current = null;
    resetBootstrapRepairState();
    bootstrapConnectorWizardRevisionRef.current = 0;
    setConnectorWizardRequest(null);
    setBootstrapError('');
    setBootstrapTimedOut(false);
    setDatasourceUploadError('');
    setBootstrapRepairPrompt(false);
    resetBootstrapSampleState();
    setStatus('datasource');
  }, [resetBootstrapRepairState, resetBootstrapSampleState]);

  const handleBootstrapFailure = useCallback((errorMessage) => {
    if (sampleItems.length) return;
    if (bootstrapRepairInFlightRef.current) {
      return;
    }
    if (attemptBootstrapRepair(errorMessage)) {
      return;
    }
    finalizeBootstrapFailure(errorMessage, {
      reopenConnectorWizard: Boolean(bootstrapConnectorContextRef.current),
    });
  }, [attemptBootstrapRepair, finalizeBootstrapFailure, sampleItems]);

  const handleBootstrapConnectorEdit = useCallback(() => {
    openBootstrapConnectorWizard();
  }, [openBootstrapConnectorWizard]);

  const continueToQuestion = useCallback(() => {
    bootstrapSampleRunRef.current += 1;
    bootstrapSampleAcceptedRef.current = true;
    setBootstrapReady(true);
    setStatus('question');
  }, []);

  const handleBootstrapRepairRetry = useCallback(() => {
    if (bootstrapRepairInFlightRef.current) {
      return;
    }

    const latestErrorMessage = bootstrapError || datasourceUploadError || bootstrapProgress.message || '';
    if (!attemptBootstrapRepair(latestErrorMessage)) {
      openBootstrapConnectorWizard();
    }
  }, [attemptBootstrapRepair, bootstrapError, bootstrapProgress.message, datasourceUploadError, openBootstrapConnectorWizard]);

  const resetWizardState = useCallback((nextStatus = 'datasource') => {
    shouldAwaitCreationUpdatesRef.current = false;
    temporaryProjectPromiseRef.current = null;
    bootstrapConnectorContextRef.current = null;
    resetBootstrapRepairState();
    bootstrapConnectorWizardRevisionRef.current = 0;

    setStatus(nextStatus);
    setGoal('');
    setUpdateMessage('');
    setTemporaryCaseId(null);
    setPendingCaseId(null);
    setAutoOpenConnector('');
    setConnectorWizardRequest(null);
    setIsPreparingTemporaryProject(false);
    setIsUploadingDatasource(false);
    setUploadProgress(0);
    setUploadedDatasourceFiles([]);
    setDatasourceUploadError('');
    setBootstrapError('');
    setBootstrapTimedOut(false);
    setIsAudioReviewOpen(false);
    clearPendingAudioReview();
    resetBootstrapSampleState();
    setBootstrapProgress({
      phase: 'waiting',
      processedCount: 0,
      queuedCount: 0,
      usableCount: 0,
      failedCount: 0,
      targetCount: BOOTSTRAP_TARGET_COUNT,
      message: '',
      latestItem: null,
      sourceType: '',
      startedAt: null,
      lastUpdatedAt: null,
    });

    clearCaseCreation();
  }, [clearPendingAudioReview, resetBootstrapRepairState, resetBootstrapSampleState]);

  const messageHandler = useCallback((parsedMessage) => {
    switch (parsedMessage.command) {
      case 'getcase': {
        if (onboarding && resumeCaseId && !temporaryCaseId && !parsedMessage.data?.case) {
          setOnboardingInvalidResume(true);
          setUploadedDatasourceFiles([]);
          setOnboardingConfirmedFiles([]);
          setStatus('datasource');
          setOnboardingGenerationError(t('first_result.restore_error'));
          break;
        }
        const incomingCaseId = parsedMessage.data?.case?.id;
        const matchesResumeCase = resumeCaseId && String(incomingCaseId) === String(resumeCaseId);
        const matchesTemporaryCreationCase = temporaryCaseId && String(incomingCaseId) === String(temporaryCaseId);
        if (!matchesResumeCase && !matchesTemporaryCreationCase) {
          break;
        }
        if (!parsedMessage.data?.case) {
          setDatasourceUploadError(t('addcase.datasource_upload_error', 'Unable to load the project creation flow.'));
          resetWizardState('datasource');
          navigate(onboarding ? '/add?onboarding=true' : '/add', { replace: true });
          break;
        }
        const handled = applyResumeCaseState(parsedMessage.data, {
          preserveBootstrapContext: Boolean(matchesTemporaryCreationCase),
        });
        if (!handled) {
          if (onboarding) {
            onboardingCompletedRef.current = true;
            clearFirstResultDraft(onboardingStorage);
            saveProjectViewForSession(incomingCaseId, DASHBOARD_PROJECT_VIEW);
          }
          navigate(`/case/${incomingCaseId}`, { replace: true });
        }
        break;
      }
      case 'forceRunDatasourceComplete':
        if (String(parsedMessage.data?.status || '').toLowerCase() === 'success') {
          if (
            status === 'bootstrap'
            && temporaryCaseId
            && typeof sendMessage === 'function'
          ) {
            // Some connectors create events directly during force-run without
            // sending bootstrap progress updates. Re-read the temporary case so
            // the creation wizard can advance as soon as the case state is ready.
            sendMessage({ command: 'getcase', caseid: temporaryCaseId });
          }
        } else if (String(parsedMessage.data?.status || '').toLowerCase() === 'error') {
          handleBootstrapFailure(
            parsedMessage.data?.message || t('addcase.bootstrap_connector_error', 'Connector ingestion failed.')
          );
        }
        break;
      case 'forceRunDatasource':
        if (String(parsedMessage.data?.status || '').toLowerCase() === 'success') {
          if (status === 'bootstrap' && temporaryCaseId) {
            setBootstrapProgress((current) => ({
              ...current,
              phase: 'restarting',
              message: t(
                'addcase.bootstrap_restart_message',
                'The connector was repaired. Restarting the bootstrap now...'
              ),
            }));
          }
        } else if (String(parsedMessage.data?.status || '').toLowerCase() === 'error') {
          handleBootstrapFailure(
            parsedMessage.data?.message || t('addcase.bootstrap_connector_error', 'Connector ingestion failed.')
          );
        }
        break;
      case 'repairGeneratedPlugin':
        if (bootstrapRepairAttemptCountRef.current > 0 || bootstrapRepairInFlightRef.current) {
          retryBootstrapAfterRepair(parsedMessage.data || {});
        }
        break;
      case 'CaseCreationBootstrapStatus': {
        const bootstrapCaseId = parsedMessage.data?.caseid;
        if (!bootstrapCaseId || String(bootstrapCaseId) !== String(temporaryCaseId)) {
          break;
        }
        if (!bootstrapSampleActiveRef.current || bootstrapSampleAcceptedRef.current) {
          break;
        }
        if (shouldAwaitCreationUpdatesRef.current) {
          break;
        }

        const nextProcessedCount = Number.isFinite(Number(parsedMessage.data?.processedCount))
          ? Number(parsedMessage.data.processedCount)
          : 0;
        const nextQueuedCount = Number.isFinite(Number(parsedMessage.data?.queuedCount))
          ? Number(parsedMessage.data.queuedCount)
          : 0;
        const nextTargetCount = Number.isFinite(Number(parsedMessage.data?.targetCount))
          ? Number(parsedMessage.data.targetCount)
          : BOOTSTRAP_TARGET_COUNT;
        const nextUsableCount = Number.isFinite(Number(parsedMessage.data?.usableCount))
          ? Number(parsedMessage.data.usableCount)
          : 0;
        const nextFailedCount = Number.isFinite(Number(parsedMessage.data?.failedCount))
          ? Number(parsedMessage.data.failedCount)
          : 0;
        const incomingStatus = String(parsedMessage.data?.status || '').toLowerCase();
        if (incomingStatus === 'error') {
          handleBootstrapFailure(
            parsedMessage.data?.message || t('addcase.bootstrap_ingestion_error', 'Datasource ingestion failed.')
          );
          break;
        }
        const incomingReady = incomingStatus === 'ready';
        const incomingLatestItem = parsedMessage.data?.latestItem || null;
        const hasIncomingProgress = incomingReady
          || nextProcessedCount > 0
          || Boolean(incomingLatestItem);
        const shouldKeepReady = bootstrapReady || incomingReady;

        setBootstrapTimedOut((currentTimedOut) => (
          currentTimedOut && !hasIncomingProgress ? true : false
        ));

        setBootstrapProgress((current) => ({
          ...current,
          phase: incomingReady
            ? 'ready'
            : nextProcessedCount > 0
              ? 'scanning'
              : current.phase,
          processedCount: shouldKeepReady
            ? Math.max(
              current.processedCount || 0,
              nextProcessedCount,
              resolveBootstrapTargetCount(nextQueuedCount, nextTargetCount)
            )
            : nextProcessedCount,
          queuedCount: nextQueuedCount,
          usableCount: nextUsableCount,
          failedCount: nextFailedCount,
          targetCount: resolveBootstrapTargetCount(nextQueuedCount, nextTargetCount),
          message: bootstrapTimedOut && !hasIncomingProgress
            ? current.message
            : parsedMessage.data?.message || current.message,
          latestItem: incomingLatestItem || current.latestItem,
          startedAt: parseBootstrapTimestamp(parsedMessage.data?.createdAt)
            || current.startedAt
            || Date.now(),
          lastUpdatedAt: parseBootstrapTimestamp(parsedMessage.data?.updatedAt)
            || (hasIncomingProgress ? Date.now() : current.lastUpdatedAt),
        }));

        if (incomingReady) {
          setBootstrapReady(true);
        }
        if (incomingReady || nextProcessedCount > 0) {
          setBootstrapRepairPrompt(false);
        }

        if (shouldKeepReady) {
          setBootstrapError('');
          setStatus('bootstrap');
        } else {
          setStatus('bootstrap');
        }
        break;
      }
      case 'FinalizeProjectCreation':
        if (onboarding && !shouldAwaitCreationUpdatesRef.current) break;
        if (onboarding && parsedMessage.data?.id && String(parsedMessage.data.id) !== String(temporaryCaseId)) break;
        if (parsedMessage.data?.status === 'success') {
          setPendingCaseId(parsedMessage.data.id || temporaryCaseId);
          setStatus('updating');
        } else {
          shouldAwaitCreationUpdatesRef.current = false;
          clearCaseCreation();
          setOnboardingGenerationError(
            parsedMessage.data?.message
            || t(onboarding ? 'first_result.generation_error' : 'addcase.flow_creator_error', 'Unable to create the project.')
          );
          setStatus('question');
        }
        break;
      case 'CaseCreationReady': {
        const readyCaseId = parsedMessage.data?.caseid || pendingCaseId || temporaryCaseId;
        if (!readyCaseId) {
          break;
        }
        if (onboarding && String(readyCaseId) !== String(pendingCaseId || temporaryCaseId)) break;
        if (!pendingCaseId && !isCaseCreationActive()) {
          break;
        }
        setPendingCaseId(null);
        clearCaseCreation();
        notifyProjectCreated({ id: readyCaseId });
        if (onboarding) {
          onboardingCompletedRef.current = true;
          clearFirstResultDraft(onboardingStorage);
          saveProjectViewForSession(readyCaseId, DASHBOARD_PROJECT_VIEW);
        }
        if (parsedMessage.data?.reviewWidgets) {
          navigate(`/case/${readyCaseId}?reviewWidgets=true`);
        } else {
          navigate(`/case/${readyCaseId}`);
        }
        break;
      }
      case 'UpdateCreationCase': {
        if (!isCaseCreationActive()) {
          break;
        }
        setStatus('updating');
        setUpdateMessage(parsedMessage.data?.message || '');
        break;
      }
      case 'datasourceIngestionUpdate': {
        const matchesCase = parsedMessage.data?.caseid
          ? String(parsedMessage.data.caseid) === String(temporaryCaseId)
          : false;
        if (!matchesCase || status !== 'bootstrap') {
          break;
        }

        const ingestionStatus = String(parsedMessage.data?.status || '').toLowerCase();
        if (ingestionStatus === 'error') {
          handleBootstrapFailure(
            parsedMessage.data?.message || t('addcase.bootstrap_ingestion_error', 'Datasource ingestion failed.')
          );
        } else if (ingestionStatus === 'warning' || ingestionStatus === 'info') {
          setBootstrapProgress((current) => ({
            ...current,
            message: parsedMessage.data?.message || current.message,
            lastUpdatedAt: Date.now(),
          }));
        } else if (ingestionStatus === 'scanning') {
          setBootstrapProgress((current) => ({
            ...current,
            phase: 'scanning',
            message: t(
              'addcase.bootstrap_scanning_message',
              'Scanning the datasource to capture the first items for project setup...'
            ),
            lastUpdatedAt: Date.now(),
          }));
        } else if (ingestionStatus === 'idle' && !isUploadingDatasource) {
          setBootstrapProgress((current) => ({
            ...current,
            phase: 'waiting',
            message: current.message || t(
              'addcase.bootstrap_waiting_message',
              'Waiting for the first datasource items to be ready...'
            ),
            lastUpdatedAt: Date.now(),
          }));
        }
        break;
      }
      case 'certification':
      case 'configuration_data':
        if (parsedMessage.data?.server_version) {
          setServerVersion(parsedMessage.data.server_version);
        }
        break;
      default:
        break;
    }
  }, [applyResumeCaseState, bootstrapReady, bootstrapTimedOut, handleBootstrapFailure, handleDatasourceUploadResult, isUploadingDatasource, navigate, onboarding, onboardingStorage, pendingCaseId, resetWizardState, resumeCaseId, retryBootstrapAfterRepair, sendMessage, status, t, temporaryCaseId]);

  useEffect(() => {
    if (lastResumeCaseIdRef.current === resumeCaseId) {
      return;
    }

    lastResumeCaseIdRef.current = resumeCaseId;
    resetWizardState(resumeCaseId ? 'updating' : 'datasource');
  }, [resetWizardState, resumeCaseId]);

  useEffect(() => {
    const unsubscribe = subscribeToMessages(messageHandler);
    return unsubscribe;
  }, [messageHandler, subscribeToMessages]);

  useEffect(() => {
    if (!onboarding || onboardingCompletedRef.current) return;
    saveFirstResultDraft(onboardingStorage, {
      goal,
      caseId: temporaryCaseId || (onboardingInvalidResume ? null : resumeCaseId),
      files: onboardingConfirmedFiles,
    });
  }, [goal, onboarding, onboardingConfirmedFiles, onboardingInvalidResume, onboardingStorage, resumeCaseId, temporaryCaseId]);

  useEffect(() => {
    if (!resumeCaseId || !sendMessage) {
      return;
    }
    setStatus('updating');
    setUpdateMessage(t('addcase.creating_project', 'Creating your project...'));
    sendMessage({ command: 'getcase', caseid: resumeCaseId });
  }, [resumeCaseId, sendMessage, t]);

  useEffect(() => {
    if (status !== 'bootstrap' || !temporaryCaseId || typeof sendMessage !== 'function') {
      return undefined;
    }
    const intervalId = setInterval(() => {
      sendMessage({ command: 'getcase', caseid: temporaryCaseId });
    }, 5000);
    return () => clearInterval(intervalId);
  }, [sendMessage, status, temporaryCaseId]);

  useEffect(() => {
    if (!pendingCaseId || updateMessage) {
      return undefined;
    }
    const timeoutId = setTimeout(() => {
      setPendingCaseId(null);
      clearCaseCreation();
      navigate(`/case/${pendingCaseId}`);
    }, 240000);
    return () => clearTimeout(timeoutId);
  }, [navigate, pendingCaseId, updateMessage]);

  const canFinalizeProject = Boolean(temporaryCaseId) && Boolean(goal.trim());
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: onboarding ? files => setOnboardingPendingFiles(current => mergeDatasourceFiles(current, files)) : handleDatasourceDrop,
    noClick: false,
    multiple: true,
    disabled: onboarding && (isUploadingDatasource || isPreparingTemporaryProject || status === 'updating'),
  });

  useEffect(() => {
    if (status !== 'bootstrap') {
      return undefined;
    }
    const startedAt = parseBootstrapTimestamp(bootstrapProgress.startedAt) || Date.now();
    setBootstrapClock(Date.now());
    const clockId = setInterval(() => setBootstrapClock(Date.now()), 1000);
    if (bootstrapTimedOut || bootstrapReady) {
      return () => clearInterval(clockId);
    }

    const timeoutDelay = Math.max(0, BOOTSTRAP_TIMEOUT_MS - (Date.now() - startedAt));
    const timeoutId = setTimeout(() => {
      const timeoutMessage = t(
        'addcase.bootstrap_timeout_progress_message',
        'We have not received any sample items yet. We will keep trying, but you can continue to your goal.'
      );
      setBootstrapTimedOut(true);
      setBootstrapProgress((current) => ({
        ...current,
        message: timeoutMessage,
        startedAt: current.startedAt || startedAt,
        lastUpdatedAt: current.lastUpdatedAt || Date.now(),
      }));
      if (bootstrapConnectorContextRef.current && !bootstrapRepairInFlightRef.current && !bootstrapReady) {
        setBootstrapRepairPrompt(true);
      }
    }, timeoutDelay);
    return () => {
      clearInterval(clockId);
      clearTimeout(timeoutId);
    };
  }, [bootstrapProgress.startedAt, bootstrapReady, bootstrapTimedOut, status, t]);

  const renderBrand = () => (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        mb: 4,
        mt: { xs: 2, sm: 4 },
      }}
    >
      <Box
        sx={{
          position: 'relative',
          mb: 2,
          animation: 'fadeInUp 1s ease-out',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            inset: '-20px',
            borderRadius: '50%',
            background: `radial-gradient(circle, ${primaryColorTransparent} 0%, transparent 70%)`,
            animation: 'pulse 3s infinite ease-in-out',
            opacity: 0.4,
          }}
        />
        <img
          src={logo}
          alt="DataUnfold Logo"
          style={{
            width: '100px',
            height: '100px',
            animation: 'glow 4s infinite ease-in-out, float 8s infinite ease-in-out',
            position: 'relative',
            zIndex: 2,
            filter: `drop-shadow(0 0 20px ${primaryColorTransparent})`,
          }}
        />
      </Box>
    </Box>
  );

  const creationSteps = onboarding ? [
    t('saas.welcome_data_title'),
    t('saas.welcome_sample_title'),
    t('saas.welcome_goal_title'),
  ] : [
    t('addcase.creation_step_datasource', 'Data'),
    t('addcase.creation_step_bootstrap', 'Bootstrap'),
    t('addcase.creation_step_question', 'Question'),
  ];

  const connectorWizardHost = connectorWizardRequest?.type ? (
    <React.Suspense fallback={<LinearProgress />}>
      <ConnectorWizardHost
        key={`${connectorWizardRequest?.type}:${connectorWizardRequest?.wizardKey || 0}`}
        open={Boolean(connectorWizardRequest?.type)}
        caseid={temporaryCaseId}
        connectorType={connectorWizardRequest?.type || ''}
        connectorTitle={connectorWizardRequest?.title || connectorWizardRequest?.type || ''}
        onClose={() => setConnectorWizardRequest(null)}
        onDatasourceConfigured={(details) => {
          setConnectorWizardRequest(null);
          handleDatasourceConfigured(details);
        }}
        beforeOpen={handleDatasourceBeforeOpen}
        ensureCase={ensureTemporaryProject}
        projectCreationWizardMode
        wizardKey={connectorWizardRequest?.wizardKey || 0}
        aiGenerated={Boolean(connectorWizardRequest?.created || connectorWizardRequest?.plan)}
        guideUrl={connectorWizardRequest?.guideUrl || ''}
        pluginOAuth={connectorWizardRequest?.pluginOAuth || null}
        pluginMemory={connectorWizardRequest?.pluginMemory || null}
        loadFailed={Boolean(connectorWizardRequest?.loadFailed)}
        loadError={connectorWizardRequest?.loadError || ''}
        setupRequest={connectorWizardRequest?.setupRequest || ''}
        setupAnswers={connectorWizardRequest?.setupAnswers}
        intentConfirmed={connectorWizardRequest?.setupIntentConfirmed === true}
        setupStorageRefs={connectorWizardRequest?.setupStorageRefs}
        setupSuggestion={connectorWizardRequest?.setupSuggestion || null}
      />
    </React.Suspense>
  ) : null;

  const renderCreationStepper = (activeStep) => (
    <Box sx={{ px: { xs: 0, sm: 2 }, mb: 4 }}>
      <Stepper activeStep={activeStep} alternativeLabel>
        {creationSteps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>
    </Box>
  );

  const renderBootstrapStage = ({ title, detail, state }) => {
    const isComplete = state === 'complete';
    const isError = state === 'error';
    const color = isComplete ? '#2e7d32' : isError ? '#c62828' : primaryColor;
    const icon = isComplete
      ? <CheckCircleOutlineIcon sx={{ color, fontSize: 28 }} />
      : isError
        ? <ErrorOutlineIcon sx={{ color, fontSize: 28 }} />
        : <PlayCircleOutlineIcon sx={{ color, fontSize: 28 }} />;

    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1.5,
          p: 1.5,
          borderRadius: 2,
          backgroundColor: state === 'active' ? 'rgba(87, 87, 176, 0.06)' : 'transparent',
        }}
      >
        <Box sx={{ display: 'flex', pt: 0.15 }}>{icon}</Box>
        <Box>
          <Typography variant="body1" sx={{ fontWeight: 700, color: isError ? '#9b2c2c' : '#263238' }}>
            {title}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.5 }}>
            {detail}
          </Typography>
        </Box>
      </Box>
    );
  };

  const bootstrapTargetCount = Math.max(1, Number(bootstrapProgress.targetCount) || BOOTSTRAP_TARGET_COUNT);
  const bootstrapProcessedCount = Math.min(
    bootstrapTargetCount,
    Math.max(0, Number(bootstrapProgress.processedCount) || 0)
  );
  const bootstrapFailedCount = Math.max(0, Number(bootstrapProgress.failedCount) || 0);
  const bootstrapProgressValue = Math.min(
    100,
    Math.round((bootstrapProcessedCount / bootstrapTargetCount) * 100)
  );
  const bootstrapElapsedMs = bootstrapProgress.startedAt
    ? Math.max(0, bootstrapClock - (parseBootstrapTimestamp(bootstrapProgress.startedAt) || bootstrapClock))
    : 0;
  const bootstrapHasSource = Boolean(
    bootstrapProgress.sourceType
    || bootstrapProgress.queuedCount
    || uploadedDatasourceFiles.length
    || isPreparingTemporaryProject
  );
  const bootstrapSampleReady = bootstrapReady || bootstrapProgress.phase === 'ready';
  const bootstrapStatusTitle = bootstrapError && !sampleItems.length
    ? t('addcase.bootstrap_friendly_error_title', 'We could not read your data')
    : bootstrapTimedOut
      ? t('addcase.bootstrap_friendly_timeout_title', 'This is taking longer than expected')
      : bootstrapSampleReady
        ? t('addcase.bootstrap_friendly_ready_title', 'Your data is ready')
        : t('addcase.bootstrap_friendly_working_title', 'We are reading your data');
  const bootstrapStatusDescription = bootstrapError && !sampleItems.length
    ? t('addcase.bootstrap_friendly_error_message', 'We could not read a sample from this source. You can review the source or continue and set your goal.')
    : bootstrapTimedOut
      ? t('addcase.bootstrap_friendly_timeout_message', 'Nothing has come through yet. You can keep waiting, continue to your goal, or go back and check the source.')
      : bootstrapSampleReady
        ? t('addcase.bootstrap_friendly_ready_message', 'We have enough information to help set up your project.')
        : t('addcase.bootstrap_friendly_working_message', 'We are reading a small sample so we can suggest a project name, tags, and widgets. This normally takes a few minutes.');

  if (onboarding) {
    const working = isUploadingDatasource || isPreparingTemporaryProject;
    const sourceReady = Boolean(temporaryCaseId && onboardingHasSource && bootstrapReady && !datasourceUploadError && !bootstrapError);
    return <>
      <FirstResultSetup
        goal={goal}
        onGoalChange={handleOnboardingGoalChange}
        files={uploadedDatasourceFiles}
        pendingFiles={onboardingPendingFiles}
        onRemovePendingFile={index => setOnboardingPendingFiles(files => files.filter((_, fileIndex) => fileIndex !== index))}
        getRootProps={getRootProps} getInputProps={getInputProps} isDragActive={isDragActive}
        working={working} generating={status === 'updating'} sourceReady={sourceReady}
        preparingRequest={onboardingPreparingRequestRef.current}
        reading={status === 'bootstrap' && !sourceReady} slow={bootstrapTimedOut}
        restoring={Boolean(resumeCaseId && !temporaryCaseId)}
        sampleItems={sampleItems} sampleReadError={sampleReadError}
        sourceError={datasourceUploadError || bootstrapError}
        onRetryUpload={onboardingRetryFilesRef.current.length ? () => handleDatasourceDrop(onboardingRetryFilesRef.current) : undefined}
        onEditSource={bootstrapConnectorContextRef.current ? handleBootstrapConnectorEdit : undefined}
        generationError={onboardingGenerationError} onGenerate={sourceReady ? handleFinalizeProject : handlePrepareOnboardingRequest}
        onExample={() => {
          if (!goal.trim()) {
            onboardingGoalEditedRef.current = true;
            setGoal(t('first_result.example_goal'));
          }
          setOnboardingPendingFiles([new File([t('first_result.example_content')], 'dataunfold-example.txt', { type: 'text/plain' })]);
        }}
        onUseTemplate={() => setTemplateSelectionOpen(true)} canStart={onboardingCanStart} notice={onboardingNotice}
        showConnectors={onboardingConnectorsOpen} onOpenConnectors={() => setOnboardingConnectorsOpen(open => !open)}
        connectors={<React.Suspense fallback={<LinearProgress />}>
          <SettingDatasources caseid={temporaryCaseId} hideHeader compactMode showDescription={false}
            setupRequest={onboardingSetupRequest} onSetupRequestClose={() => { setOnboardingSetupRequest(null); setOnboardingConnectorsOpen(false); }}
            onSetupRequestChange={handleOnboardingGoalChange}
            compactLauncherBrowseLabel={t('first_result.browse_accounts')} compactLauncherCreatorLabel={t('first_result.other_account')}
            autoOpenConnector={autoOpenConnector} onConnectorOpened={() => setAutoOpenConnector('')}
            onConnectorCreated={details => {
              if (details?.type) openBootstrapConnectorWizard(updateBootstrapConnectorContext({ ...details, caseid: temporaryCaseId }));
            }}
            onDatasourceConfigured={handleDatasourceConfigured} beforeOpenConnector={handleDatasourceBeforeOpen}
            ensureCase={ensureTemporaryProject} projectCreationWizardMode />
        </React.Suspense>}
      />
      {connectorWizardHost}
      <TemplateSelectionDialog open={templateSelectionOpen} onClose={() => setTemplateSelectionOpen(false)} onSelectTemplate={() => {}} onStartBlankProject={() => setTemplateSelectionOpen(false)} />
    </>;
  }

  if (status === 'updating') {
    return <LoadingScreen status={status} updateMessage={updateMessage} />;
  }

  if (status === 'bootstrap') {
    return (
      <>
        <Box
        className="container"
        sx={{
          backgroundColor: '#f7f7ff',
          background: 'linear-gradient(135deg, #f7f7ff 0%, #ffffff 100%)',
          backgroundImage: 'linear-gradient(135deg, #f7f7ff 0%, #ffffff 70%, rgba(87, 87, 176, 0.08) 100%)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 4,
          position: 'relative',
        }}
      >
        <Box sx={{ maxWidth: '960px', width: '100%', mx: 'auto' }}>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 3, sm: 5 },
              borderRadius: '28px',
              background: 'linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(247,247,255,0.96) 100%)',
              boxShadow: '0 18px 40px rgba(87, 87, 176, 0.12)',
              border: '1px solid rgba(87, 87, 176, 0.08)',
            }}
          >
            {renderBrand()}
            <Typography
              variant="h4"
              sx={{ color: primaryColor, fontWeight: 700, textAlign: 'center', mb: 1.5 }}
            >
              {onboarding ? t('saas.welcome_sample_title') : t('addcase.bootstrap_title', 'Let’s get your project ready')}
            </Typography>
            <Typography
              variant="body1"
              sx={{ color: 'text.secondary', textAlign: 'center', maxWidth: '760px', mx: 'auto', mb: 4, lineHeight: 1.7 }}
            >
              {t(
                'addcase.bootstrap_subtitle',
                'We’ve received your data. We’re reading a small sample so we can suggest a project name, tags, and widgets for you.'
              )}
            </Typography>

            {renderCreationStepper(1)}

            <Box
              sx={{
                borderRadius: '22px',
                border: '1px solid rgba(87, 87, 176, 0.12)',
                background: 'rgba(255,255,255,0.82)',
                p: { xs: 3, sm: 4 },
              }}
            >
              <Box sx={{ textAlign: 'center', mb: 3 }}>
                <Typography variant="h6" sx={{ color: primaryColor, fontWeight: 700, mb: 0.75 }}>
                  {bootstrapStatusTitle}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', maxWidth: 680, mx: 'auto', lineHeight: 1.6 }}>
                  {bootstrapStatusDescription}
                </Typography>
              </Box>

              <Box sx={{ maxWidth: '680px', mx: 'auto', mb: 3 }}>
                {renderBootstrapStage({
                  title: t('addcase.bootstrap_stage_received_title', 'Data received'),
                  detail: bootstrapHasSource
                    ? t('addcase.bootstrap_stage_received_detail', 'Your source is connected and ready to be read.')
                    : t('addcase.bootstrap_stage_received_waiting', 'Waiting for the source to finish connecting.'),
                  state: bootstrapHasSource ? 'complete' : 'active',
                })}
                {renderBootstrapStage({
                  title: t('addcase.bootstrap_stage_reading_title', 'Reading a small sample'),
                  detail: bootstrapSampleReady
                    ? t('addcase.bootstrap_stage_reading_complete', 'The sample is ready.')
                    : t('addcase.bootstrap_stage_reading_detail', '{{processed}} of {{target}} items processed.', {
                      processed: bootstrapProcessedCount,
                      target: bootstrapTargetCount,
                    }),
                  state: sampleItems.length ? 'complete' : bootstrapError ? 'error' : 'active',
                })}
                {renderBootstrapStage({
                  title: t('addcase.bootstrap_stage_goal_title', 'Set your goal'),
                  detail: bootstrapSampleReady
                    ? t('addcase.bootstrap_stage_goal_ready', 'Your question is ready for you.')
                    : t('addcase.bootstrap_stage_goal_waiting', 'This opens when the sample is ready.'),
                  state: bootstrapSampleReady ? 'complete' : 'pending',
                })}
              </Box>

              {sampleReadError && (
                <Alert severity="warning" sx={{ mb: 2 }}>{t('creation_sample.connection_error')}</Alert>
              )}
              {sampleItems.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" sx={{ mb: 1 }}>{t('creation_sample.title')}</Typography>
                  <Typography sx={{ mb: 2 }}>{t('creation_sample.description')}</Typography>
                  <CreationSample items={sampleItems} />
                  <Button variant="contained" onClick={continueToQuestion}>{t('creation_sample.accept')}</Button>
                  <Button onClick={returnToDatasource}>{t('creation_sample.change')}</Button>
                </Box>
              )}
              {bootstrapRepairPrompt && !bootstrapError && !sampleItems.length && (
                <Alert
                  severity="warning"
                  sx={{ maxWidth: '720px', mx: 'auto', mb: 3, alignItems: 'center' }}
                  action={(
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                      {bootstrapConnectorContextRef.current && !bootstrapRepairInFlightRef.current && (
                        <Button
                          color="inherit"
                          size="small"
                          onClick={handleBootstrapRepairRetry}
                        >
                          {t('addcase.bootstrap_try_repair', 'Try repair')}
                        </Button>
                      )}
                      {bootstrapConnectorContextRef.current && !bootstrapRepairInFlightRef.current && (
                        <Button
                          color="inherit"
                          size="small"
                          onClick={handleBootstrapConnectorEdit}
                        >
                          {t('addcase.bootstrap_edit_connector', 'Edit connector')}
                        </Button>
                      )}
                      <Button
                        color="inherit"
                        size="small"
                        onClick={continueToQuestion}
                      >
                        {t('addcase.bootstrap_continue_to_goal', 'Continue to my goal')}
                      </Button>
                      <Button
                        color="inherit"
                        size="small"
                        onClick={returnToDatasource}
                      >
                        {t('addcase.bootstrap_back_to_datasource', 'Back to datasource')}
                      </Button>
                    </Box>
                  )}
                >
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {t('addcase.bootstrap_timeout_title', 'This is taking longer than expected')}
                  </Typography>
                  <Typography variant="body2">
                    {t(
                      'addcase.bootstrap_timeout_repair_message',
                      'You can ask DataUnfold to check the source, continue to your goal, or go back and choose another source.'
                    )}
                  </Typography>
                </Alert>
              )}

              {bootstrapTimedOut && !bootstrapRepairPrompt && !bootstrapError && !sampleItems.length && (
                <Alert
                  severity="warning"
                  sx={{ maxWidth: '720px', mx: 'auto', mb: 3, alignItems: 'center' }}
                  action={(
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                      <Button color="inherit" size="small" onClick={continueToQuestion}>
                        {t('addcase.bootstrap_continue_to_goal', 'Continue to my goal')}
                      </Button>
                      <Button color="inherit" size="small" onClick={returnToDatasource}>
                        {t('addcase.bootstrap_back_to_datasource', 'Back to data')}
                      </Button>
                    </Box>
                  )}
                >
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {t('addcase.bootstrap_timeout_title', 'This is taking longer than expected')}
                  </Typography>
                  <Typography variant="body2">
                    {t(
                      'addcase.bootstrap_timeout_message',
                      'Nothing has come through yet. You have been waiting {{elapsed}}.',
                      { elapsed: formatBootstrapElapsed(bootstrapElapsedMs, t) }
                    )}
                  </Typography>
                </Alert>
              )}

              {bootstrapError && !sampleItems.length && (
                <Alert
                  severity="error"
                  sx={{ maxWidth: '720px', mx: 'auto', mb: 3, alignItems: 'center' }}
                  action={(
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                      {bootstrapConnectorContextRef.current && !bootstrapRepairInFlightRef.current && (
                        <Button
                          color="inherit"
                          size="small"
                          onClick={handleBootstrapConnectorEdit}
                        >
                          {t('addcase.bootstrap_edit_connector', 'Edit connector')}
                        </Button>
                      )}
                      {bootstrapConnectorContextRef.current
                        && !bootstrapRepairInFlightRef.current
                        && bootstrapRepairAttemptCountRef.current < BOOTSTRAP_REPAIR_MAX_ATTEMPTS && (
                        <Button
                          color="inherit"
                          size="small"
                          onClick={handleBootstrapRepairRetry}
                        >
                          {t('addcase.bootstrap_retry_repair', 'Retry repair')}
                        </Button>
                      )}
                      <Button
                        color="inherit"
                        size="small"
                        onClick={continueToQuestion}
                      >
                        {t('addcase.bootstrap_continue_to_goal', 'Continue to my goal')}
                      </Button>
                      <Button
                        color="inherit"
                        size="small"
                        onClick={returnToDatasource}
                      >
                        {t('addcase.bootstrap_back_to_datasource', 'Back to datasource')}
                      </Button>
                    </Box>
                  )}
                >
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {t('addcase.bootstrap_friendly_error_title', 'We could not read your data')}
                  </Typography>
                  <Typography variant="body2">
                    {bootstrapError}
                  </Typography>
                  {bootstrapConnectorContextRef.current
                    && bootstrapRepairAttemptCountRef.current >= BOOTSTRAP_REPAIR_MAX_ATTEMPTS && (
                    <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                      {t(
                        'addcase.bootstrap_repair_limit_reached',
                        'Automatic repair reached the limit. Open the connector to edit it and try again.'
                      )}
                    </Typography>
                  )}
                </Alert>
              )}

              {(isUploadingDatasource || isPreparingTemporaryProject) && (
                <Box sx={{ maxWidth: '560px', mx: 'auto', mb: 3 }}>
                  <LinearProgress
                    variant={isUploadingDatasource ? 'determinate' : 'indeterminate'}
                    value={Math.max(5, Math.min(100, uploadProgress))}
                    sx={{
                      height: 10,
                      borderRadius: 999,
                      backgroundColor: 'rgba(87, 87, 176, 0.12)',
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: primaryColor,
                      },
                    }}
                  />
                  <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary', textAlign: 'center' }}>
                    {isPreparingTemporaryProject
                      ? t('addcase.datasource_preparing_project', 'Preparing the temporary project...')
                      : t('addcase.datasource_uploading', 'Uploading datasource files...')}
                  </Typography>
                </Box>
              )}

              <Box sx={{ maxWidth: '620px', mx: 'auto' }}>
                <LinearProgress
                  variant="determinate"
                  value={bootstrapProgressValue}
                  sx={{
                    height: 12,
                    borderRadius: 999,
                    backgroundColor: 'rgba(87, 87, 176, 0.10)',
                    '& .MuiLinearProgress-bar': {
                      background: `linear-gradient(to right, ${primaryColor}, ${hoverColor})`,
                    },
                  }}
                />
                <Typography variant="body2" sx={{ mt: 1.25, color: 'text.secondary', textAlign: 'center' }}>
                  {t(
                    'addcase.bootstrap_counter',
                    '{{count}} of {{target}} sample items processed',
                    { count: bootstrapProcessedCount, target: bootstrapTargetCount }
                  )}
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1.5, flexWrap: 'wrap', mt: 1 }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {t('addcase.bootstrap_elapsed', 'Started {{elapsed}} ago', {
                      elapsed: formatBootstrapElapsed(bootstrapElapsedMs, t),
                    })}
                  </Typography>
                  {bootstrapFailedCount > 0 && (
                    <Typography variant="caption" sx={{ color: '#9b2c2c', fontWeight: 600 }}>
                      {t('addcase.bootstrap_failed_count', '{{count}} item(s) could not be read', {
                        count: bootstrapFailedCount,
                      })}
                    </Typography>
                  )}
                </Box>
              </Box>

              {bootstrapProgress.latestItem?.file_name && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2.5 }}>
                  <Chip
                    label={t(
                      'addcase.bootstrap_latest_item',
                      'Latest item: {{name}}',
                      { name: bootstrapProgress.latestItem.file_name }
                    )}
                    sx={{
                      backgroundColor: 'rgba(87, 87, 176, 0.08)',
                      border: '1px solid rgba(87, 87, 176, 0.14)',
                    }}
                  />
                </Box>
              )}
            </Box>
          </Paper>
        </Box>
        </Box>
        {connectorWizardHost}
      </>
    );
  }

  if (!resumeCaseId && !onboarding) {
    return (
      <>
        <FlowCreatorHome
          onUseTemplate={handleUseFlowTemplate}
          onStart={handleStartFlowCreator}
          onSave={handleSaveFlowProject}
          title={flowProjectTitle}
           onTitleChange={(value) => {
             setFlowProjectTitle(value);
             setFlowCreatorError('');
             setFlowCreatorNotice((current) => (
               current && String(value || '').trim() ? current : ''
             ));
           }}
           isStarting={isStartingFlowProject}
           error={isFlowTitleDialogOpen ? '' : flowCreatorError}
           notice={flowCreatorNotice}
           pendingInteraction={pendingFlowInteraction}
           focusTitleSignal={focusFlowTitleSignal}
         />
        <Dialog
          open={isFlowTitleDialogOpen}
          onClose={handleCloseFlowTitleDialog}
          disableEscapeKeyDown={isStartingFlowProject}
          fullWidth
          maxWidth="xs"
          aria-labelledby="flow-project-title-dialog"
          PaperProps={{
            component: 'form',
            onSubmit: (event) => {
              event.preventDefault();
              handleSaveFlowProject();
            },
          }}
        >
          <DialogTitle id="flow-project-title-dialog">
            {t('addcase.flow_creator_title_dialog', 'Name your project')}
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('addcase.flow_creator_title_dialog_hint', 'Enter a title to create your project and continue to your flow.')}
            </Typography>
            <TextField
              autoFocus
              fullWidth
              label={t('addcase.flow_creator_title_label', 'Project title')}
              value={flowProjectTitle}
              onChange={(event) => {
                setFlowProjectTitle(event.target.value);
                setFlowCreatorError('');
              }}
              disabled={isStartingFlowProject}
              error={Boolean(flowCreatorError)}
              helperText={flowCreatorError}
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={handleCloseFlowTitleDialog} disabled={isStartingFlowProject}>
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button type="submit" variant="contained" disabled={isStartingFlowProject}>
              {isStartingFlowProject
                ? t('addcase.flow_creator_starting', 'Saving your project...')
                : t('addcase.flow_creator_create_continue', 'Create and continue')}
            </Button>
          </DialogActions>
        </Dialog>
        <TemplateSelectionDialog
          open={templateSelectionOpen}
          onClose={() => setTemplateSelectionOpen(false)}
          onSelectTemplate={() => {}}
          onStartBlankProject={handleStartBlankProject}
          renderBehindProjectSidebar={!resumeCaseId}
        />
      </>
    );
  }

  return (
    <Box
      className="container"
      sx={{
        backgroundColor: '#f7f7ff',
        background: 'linear-gradient(135deg, #f7f7ff 0%, #ffffff 100%)',
        backgroundImage: 'linear-gradient(135deg, #f7f7ff 0%, #ffffff 70%, rgba(87, 87, 176, 0.08) 100%)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 4,
        position: 'relative',
      }}
    >
      <Box sx={{ maxWidth: '980px', width: '100%', mx: 'auto' }}>
        {status === 'datasource' ? (
          <Box sx={{ mt: 6, mb: 4 }}>
            {!resumeCaseId && !onboarding && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: 1.5,
                  mb: 2,
                  flexWrap: 'wrap',
                }}
              >
                <Button
                  variant="outlined"
                  onClick={() => setTemplateSelectionOpen(true)}
                  sx={{
                    borderRadius: '999px',
                    textTransform: 'none',
                    color: primaryColor,
                    borderColor: 'rgba(87, 87, 176, 0.24)',
                    backgroundColor: 'rgba(255,255,255,0.82)',
                  }}
                >
                  {t('addcase.use_template', 'Use Template')}
                </Button>
              </Box>
            )}
            <Box sx={{ textAlign: 'center' }}>
              {renderBrand()}
              <Typography
                variant="h4"
                component="h1"
                sx={{
                  color: primaryColor,
                  fontWeight: 600,
                  mb: 2,
                  fontSize: '2rem',
                }}
              >
                {onboarding ? t('saas.welcome_data_title') : t('addcase.datasource_title', 'Create your project')}
              </Typography>
              {onboarding && <Typography color="text.secondary" sx={{ mb: 3 }}>{t('saas.welcome_data_help')}</Typography>}
              {renderCreationStepper(0)}
            </Box>

            {datasourceUploadError && (
              <Box
                sx={{
                  mb: 3,
                  mx: 'auto',
                  maxWidth: '760px',
                  p: 2,
                  borderRadius: '18px',
                  border: '1px solid rgba(198, 40, 40, 0.2)',
                  backgroundColor: 'rgba(255, 235, 238, 0.85)',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1.25,
                }}
              >
                <WarningAmberIcon sx={{ color: '#c62828', fontSize: '1.3rem', mt: '2px' }} />
                <Typography variant="body2" sx={{ color: '#8e1c1c', fontWeight: 600 }}>
                  {datasourceUploadError}
                </Typography>
              </Box>
            )}

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    lg: 'repeat(3, minmax(0, 1fr))',
                  },
                  gap: { xs: 2.5, lg: 3 },
                  alignItems: 'start',
                }}
              >
              <Paper
                {...getRootProps({ 'data-file-drop-target': 'true' })}
                elevation={0}
                sx={{
                  ...datasourceCardSx,
                  border: isDragActive
                    ? `2px dashed ${primaryColor}`
                    : '2px dashed rgba(87, 87, 176, 0.22)',
                  background: isDragActive
                    ? 'linear-gradient(135deg, rgba(87, 87, 176, 0.12) 0%, rgba(255,255,255,0.98) 100%)'
                    : 'linear-gradient(135deg, rgba(255,255,255,0.98) 0%, rgba(247,247,255,0.96) 100%)',
                  boxShadow: isDragActive
                    ? '0 18px 42px rgba(87, 87, 176, 0.16)'
                    : '0 10px 30px rgba(87, 87, 176, 0.08)',
                  cursor: 'pointer',
                }}
              >
                <input {...getInputProps({ 'aria-label': t('addcase.datasource_drop_browse', 'Choose files') })} />
                <Box sx={datasourceCardHeaderSx}>
                  <CloudUploadIcon sx={{ fontSize: 46, color: primaryColor }} />
                  <Typography variant="h6" sx={{ color: primaryColor, fontWeight: 700 }}>
                    {isDragActive
                      ? t('addcase.datasource_drop_active', 'Drop files to create the datasource')
                      : t('addcase.datasource_drop_title', 'Drag and drop files here')}
                  </Typography>
                </Box>

                <Box sx={datasourceCardBodySx}>
                  <Typography variant="body2" sx={datasourceCardSubtitleSx}>
                    {t('addcase.datasource_drop_subtitle', 'Upload one or more files to build the first datasource for this project.')}
                  </Typography>
                  <Button
                    variant="outlined"
                    startIcon={<CloudUploadIcon fontSize="small" />}
                    sx={{
                      mt: 1,
                      ...wizardLauncherButtonSx,
                    }}
                  >
                    {t('addcase.datasource_drop_browse', 'Choose files')}
                  </Button>

                  {uploadedDatasourceFiles.length > 0 && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 1, mt: 2 }}>
                      {uploadedDatasourceFiles.slice(0, 5).map((file) => (
                        <Chip
                          key={`${file.name}-${file.size ?? 'unknown'}`}
                          label={file.size ? `${file.name} (${formatBytes(file.size)})` : file.name}
                          sx={{
                            backgroundColor: 'rgba(87, 87, 176, 0.08)',
                            border: '1px solid rgba(87, 87, 176, 0.14)',
                          }}
                        />
                      ))}
                    </Box>
                  )}

                  {isUploadingDatasource && (
                    <Box sx={{ width: '100%', maxWidth: '520px', mt: 2 }}>
                      <LinearProgress
                        variant="determinate"
                        value={Math.max(5, Math.min(100, uploadProgress))}
                        sx={{
                          height: 10,
                          borderRadius: 999,
                          backgroundColor: 'rgba(87, 87, 176, 0.12)',
                          '& .MuiLinearProgress-bar': {
                            backgroundColor: primaryColor,
                          },
                        }}
                      />
                      <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                        {t('addcase.datasource_uploading', 'Uploading datasource files...')}
                      </Typography>
                    </Box>
                  )}

                  {isPreparingTemporaryProject && !isUploadingDatasource && (
                    <Box sx={{ width: '100%', maxWidth: '520px', mt: 2 }}>
                      <LinearProgress
                        sx={{
                          height: 10,
                          borderRadius: 999,
                          backgroundColor: 'rgba(87, 87, 176, 0.12)',
                          '& .MuiLinearProgress-bar': {
                            backgroundColor: primaryColor,
                          },
                        }}
                      />
                      <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                        {t('addcase.datasource_preparing_project', 'Preparing the temporary project...')}
                      </Typography>
                    </Box>
                  )}

                </Box>
              </Paper>

              <Paper
                elevation={0}
                sx={datasourceCardSx}
              >
                <Box sx={datasourceCardHeaderSx}>
                  <MicIcon sx={{ fontSize: 46, color: primaryColor }} />
                  <Typography
                    variant="h6"
                    sx={{
                      color: primaryColor,
                      fontWeight: 700,
                    }}
                  >
                    {t('addcase.project_audio_title', 'Record audio')}
                  </Typography>
                </Box>

                <Box sx={datasourceCardBodySx}>
                  <Typography variant="body2" sx={datasourceCardSubtitleSx}>
                    {t('addcase.project_audio_subtitle', 'Record a voice note and use it as the first datasource for this project.')}
                  </Typography>

                  <Button
                    variant={isRecordingAudio ? 'contained' : 'outlined'}
                    color={isRecordingAudio ? 'error' : 'primary'}
                    startIcon={isRecordingAudio ? <StopIcon fontSize="small" /> : <MicIcon fontSize="small" />}
                    onClick={isRecordingAudio ? stopAudioRecording : startAudioRecording}
                    disabled={isUploadingDatasource || isPreparingTemporaryProject}
                    sx={{
                      mt: 1,
                      ...wizardLauncherButtonSx,
                    }}
                  >
                    {isRecordingAudio
                      ? t('addcase.project_audio_stop', 'Stop recording')
                      : t('addcase.project_audio_start', 'Start recording')}
                  </Button>

                  <Button
                    variant="outlined"
                    startIcon={<MicExternalOnIcon fontSize="small" />}
                    onClick={() => setIsMixedConsentOpen(true)}
                    disabled={isUploadingDatasource || isPreparingTemporaryProject || isRecordingAudio || !canUseDisplayAudio}
                    sx={{
                      mt: 1.25,
                      ...wizardLauncherButtonSx,
                    }}
                  >
                    {t('addcase.project_audio_start_system', 'Record system wide')}
                  </Button>

                  {pendingAudioReview?.name && !isAudioReviewOpen && (
                    <Chip
                      label={pendingAudioReview.name}
                      sx={{
                        mt: 2,
                        backgroundColor: 'rgba(87, 87, 176, 0.08)',
                        border: '1px solid rgba(87, 87, 176, 0.14)',
                        maxWidth: '100%',
                      }}
                    />
                  )}

                  {isRecordingAudio && (
                    <Typography variant="body2" sx={{ mt: 2, color: '#c62828', fontWeight: 600 }}>
                      {t('addcase.project_audio_recording_live', 'Recording... click stop when finished.')}
                    </Typography>
                  )}

                  {!canUseDisplayAudio && (
                    <Typography variant="caption" sx={{ mt: 1.5, color: 'text.secondary', maxWidth: 320 }}>
                      {t('addcase.project_audio_system_hint', 'System-wide audio capture is available on desktop browsers that support screen sharing audio.')}
                    </Typography>
                  )}
                </Box>
              </Paper>

              <Paper
                elevation={0}
                sx={datasourceCardSx}
              >
                <Box sx={datasourceCardHeaderSx}>
                  <HubIcon sx={{ fontSize: 46, color: primaryColor }} />
                  <Typography
                    variant="h6"
                    sx={{
                      color: primaryColor,
                      fontWeight: 700,
                    }}
                  >
                    {t('addcase.project_connectors_title', 'Connecteur')}
                  </Typography>
                </Box>

                <Box sx={datasourceCardBodySx}>
                  <Typography variant="body2" sx={datasourceCardSubtitleSx}>
                    {t('addcase.project_connectors_subtitle', 'Connect email, cloud storage, or another service to start with live data.')}
                  </Typography>
                  <Box sx={{ width: '100%', maxWidth: 320, mx: 'auto' }}>
                    <React.Suspense fallback={<LinearProgress />}>
                      <SettingDatasources
                        caseid={temporaryCaseId}
                        hideHeader
                        compactMode
                        showDescription={false}
                        compactLauncherBrowseLabel={t('addcase.project_connectors_browse', 'Parcourir')}
                        compactLauncherCreatorLabel={t('addcase.project_connectors_creator', 'Créateur')}
                        compactLauncherVertical
                        autoOpenConnector={autoOpenConnector}
                        onConnectorCreated={(details) => {
                          if (!details?.type) {
                            return;
                          }
                          const nextConnectorRequest = updateBootstrapConnectorContext({
                            ...details,
                            caseid: temporaryCaseId,
                          });
                          openBootstrapConnectorWizard(nextConnectorRequest);
                        }}
                        onConnectorOpened={() => {
                          setAutoOpenConnector('');
                        }}
                        onDatasourceConfigured={handleDatasourceConfigured}
                        beforeOpenConnector={handleDatasourceBeforeOpen}
                        ensureCase={ensureTemporaryProject}
                        projectCreationWizardMode
                      />
                    </React.Suspense>
                  </Box>
                  {connectorWizardHost}
                </Box>
              </Paper>
            </Box>
          </Box>
        ) : (
          <Box sx={{ textAlign: 'center', mt: 8, mb: 6 }}>
            {renderBrand()}
            {renderCreationStepper(2)}
            <Typography
              variant="h5"
              component="h2"
              sx={{
                color: primaryColor,
                fontWeight: 600,
                mb: 1.5,
                fontSize: '1.45rem',
              }}
            >
              {t(onboarding ? 'saas.welcome_goal_title' : 'addcase.question_title')}
            </Typography>

            <Typography
              variant="body2"
              sx={{
                maxWidth: '700px',
                mx: 'auto',
                mb: 2,
              }}
            >
              {t(onboarding ? 'saas.welcome_goal_description' : 'addcase.question_subtitle')}
            </Typography>

            {bootstrapTimedOut && (
              <Alert
                severity="warning"
                sx={{ maxWidth: '700px', mx: 'auto', mb: 2, textAlign: 'left' }}
              >
                {t(
                  'addcase.bootstrap_continue_notice',
                  'We are still waiting for the first sample from your data. You can set your goal now; DataUnfold will continue processing in the background.'
                )}
              </Alert>
            )}

            {onboardingGenerationError && (
              <Alert
                severity="error"
                sx={{ maxWidth: '700px', mx: 'auto', mb: 2, textAlign: 'left' }}
              >
                {onboardingGenerationError}
              </Alert>
            )}

            <Box sx={{ maxWidth: '700px', mx: 'auto', mb: 2 }}>
              <TextField
                label={onboarding ? t('saas.welcome_goal_label') : undefined}
                variant="outlined"
                fullWidth
                multiline
                rows={3}
                value={goal}
                onChange={(e) => setGoal(e.target.value.slice(0, 200))}
                placeholder={typedPlaceholder || t('addcase.input_placeholder')}
                inputProps={{ maxLength: 200 }}
                helperText={t('addcase.characters_left', { remaining: Math.max(0, 200 - goal.length) })}
                FormHelperTextProps={{
                  sx: {
                    textAlign: 'right',
                    maxWidth: '700px',
                    mx: 'auto',
                    color: 'rgba(87, 87, 176, 0.7)',
                  },
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '10px',
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    '& .MuiInputBase-inputMultiline': {
                      fontSize: '1.2rem',
                      lineHeight: 1.6,
                    },
                    '& .MuiInputBase-input::placeholder': {
                      fontSize: '1.1rem',
                      color: 'rgba(0, 0, 0, 0.38)',
                      opacity: 1,
                    },
                    '&:hover fieldset': {
                      borderColor: hoverColor,
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: primaryColor,
                      borderWidth: '2px',
                    },
                  },
                  '& .MuiInputLabel-outlined.Mui-focused': {
                    color: primaryColor,
                  },
                }}
              />
            </Box>

            <Button
              variant="contained"
              startIcon={<CreateNewFolderIcon />}
              onClick={handleFinalizeProject}
              disabled={!canFinalizeProject}
              sx={{
                px: 5,
                py: 1.5,
                fontSize: '1rem',
                borderRadius: '50px',
                boxShadow: '0 4px 14px rgba(87, 87, 176, 0.25)',
                fontWeight: 600,
                background: !canFinalizeProject
                  ? 'linear-gradient(to right, #e0e0e0, #f5f5f5)'
                  : `linear-gradient(to right, ${primaryColor}, ${hoverColor})`,
                color: !canFinalizeProject ? '#b0b0b0' : '#fff',
                opacity: !canFinalizeProject ? 0.7 : 1,
                cursor: !canFinalizeProject ? 'not-allowed' : 'pointer',
                textTransform: 'none',
                transition: 'all 0.3s ease',
                mt: 2,
                '&:hover': !canFinalizeProject
                  ? {
                      background: 'linear-gradient(to right, #e0e0e0, #f5f5f5)',
                      boxShadow: 'none',
                      transform: 'none',
                    }
                  : {
                      transform: 'translateY(-2px)',
                      boxShadow: '0 6px 20px rgba(87, 87, 176, 0.35)',
                      background: `linear-gradient(to right, ${primaryColor}, ${selectedColor})`,
                    },
              }}
            >
              {t(onboarding ? 'saas.welcome_generate' : 'addcase.create_project')}
            </Button>
          </Box>
        )}
      </Box>

      {!onboarding && <Box
        sx={{
          position: 'absolute',
          bottom: 8,
          left: 0,
          right: 0,
          textAlign: 'center',
          zIndex: 1,
        }}
      >
        <Typography variant="caption" sx={{ color: 'rgba(0,0,0,0.6)' }}>
          {serverVersion ? `V: ${serverVersion}` : 'Server: unknown'}
        </Typography>
      </Box>}

      <RecordingDialog
        open={isRecordingAudio}
        recordingElapsed={recordingElapsed}
        stopRecording={stopAudioRecording}
        waveformHistory={waveformHistory}
        maxRecordingDurationSeconds={maxRecordingDurationSeconds}
      />

      <AudioReviewDialog
        open={isAudioReviewOpen}
        onClose={() => setIsAudioReviewOpen(false)}
        onReRecord={() => {
          setIsAudioReviewOpen(false);
          clearPendingAudioReview();
          startAudioRecording();
        }}
        onUpload={handleUploadRecordedAudio}
        audioUrl={pendingAudioReview?.url}
        audioName={pendingAudioReview?.name}
        title={t('addcase.project_audio_review_title', 'Review recording')}
        hintText={t('addcase.project_audio_review_hint', 'Listen to your recording before uploading it to the new project.')}
        reRecordLabel={t('addcase.project_audio_rerecord', 'Record again')}
        cancelLabel={t('common.cancel', 'Cancel')}
        uploadLabel={t('addcase.project_audio_upload', 'Upload recording')}
      />

      <Dialog
        open={isMixedConsentOpen}
        onClose={() => setIsMixedConsentOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t('addcase.mixed_recording_consent_title', 'Record microphone + system audio?')}</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
            <WarningAmberIcon sx={{ color: '#f59e0b', fontSize: '1.75rem', mt: '2px' }} />
            <Typography variant="body2" sx={{ color: '#4b5563', display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {t(
                'addcase.mixed_recording_consent_body',
                'Recording system audio will capture other participants. Please make sure everyone on the call consents before continuing.'
              )}
              <Typography component="span" variant="caption" sx={{ color: '#8b94a0' }}>
                {t('addcase.mixed_recording_share_audio_hint', 'To capture all audio, make sure the "Share Audio" toggle in the screen-sharing prompt is on.')}
              </Typography>
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="outlined" onClick={() => setIsMixedConsentOpen(false)} sx={{ textTransform: 'none' }}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setIsMixedConsentOpen(false);
              startAudioRecording('mixed');
            }}
            sx={{ textTransform: 'none' }}
          >
            {t('addcase.mixed_recording_consent_confirm', 'Continue')}
          </Button>
        </DialogActions>
      </Dialog>

      <TemplateSelectionDialog
        open={templateSelectionOpen}
        onClose={() => setTemplateSelectionOpen(false)}
        onSelectTemplate={() => {}}
        onStartBlankProject={handleStartBlankProject}
      />
    </Box>
  );
};

export default AddProject;
